/**
 * Orovia — cinematic interactions
 */
(function () {
  'use strict';
  const qs = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => [...c.querySelectorAll(s)];

  /* Hide trailing codes like "| OB-123" / "| ORS-181" / "| OBN-113" from storefront titles */
  function displayTitle(title) {
    if (!title) return '';
    return String(title)
      .replace(/\s*\|\s*[A-Za-z]{1,6}-?[A-Za-z0-9]{0,12}\s*$/i, '')
      .trim();
  }

  function scrubDocumentTitles() {
    if (document.title && document.title.includes('|')) {
      document.title = displayTitle(document.title.replace(/\s*—\s*Orovia\s*$/i, '').trim()) + ' — Orovia';
    }
    qsa('meta[property="og:title"], meta[name="twitter:title"]').forEach(el => {
      const v = el.getAttribute('content') || '';
      if (v.includes('|')) el.setAttribute('content', displayTitle(v));
    });
  }

  function buildSpecsHtml(p) {
    const rows = [];
    if (p.material) rows.push(`<div><dt>Material</dt><dd>${escapeHtml(p.material)}</dd></div>`);
    if (p.finish) rows.push(`<div><dt>Finish</dt><dd>${escapeHtml(p.finish)}</dd></div>`);
    if (p.origin) rows.push(`<div><dt>Origin</dt><dd>${escapeHtml(p.origin)}</dd></div>`);
    if (p.sku) rows.push(`<div><dt>Reference</dt><dd>${escapeHtml(p.sku)}</dd></div>`);
    if (!rows.length) return '';
    return `<dl class="ov-pdp-page__specs">${rows.join('')}</dl>`;
  }

  function initReveal() {
    const reveal = el => el.classList.add('is-in');
    /* threshold 0 — tall grids never hit 8% visible until you scroll */
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        reveal(e.target);
        io.unobserve(e.target);
      });
    }, { threshold: 0, rootMargin: '40px 0px' });
    qsa('[data-reveal]').forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight + 40 && r.bottom > -40) reveal(el);
      else io.observe(el);
    });
  }

  function initFooterCrown() {
    qsa('[data-ov-ft-crest]').forEach(crest => {
      if (crest.dataset.ovFtCrownReady) return;
      crest.dataset.ovFtCrownReady = '1';

      const crown = qs('[data-ov-ft-crown]', crest);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const crownOn = () => {
        crest.classList.add('is-crowned');
        if (!reduce) crest.classList.add('is-crowning');
      };

      if (reduce) {
        crownOn();
        return;
      }

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(entries => {
          entries.forEach(e => {
            if (!e.isIntersecting) return;
            crownOn();
            io.unobserve(crest);
          });
        }, { threshold: 0.35, rootMargin: '0px 0px -8% 0px' });
        io.observe(crest);
      } else {
        crownOn();
      }

      if (!crown) return;

      crown.addEventListener('mousemove', e => {
        if (!crest.classList.contains('is-crowned')) return;
        const r = crown.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        crown.style.setProperty('--ov-tilt-x', `${y * -8}deg`);
        crown.style.setProperty('--ov-tilt-y', `${x * 10}deg`);
      });

      crown.addEventListener('mouseleave', () => {
        crown.style.setProperty('--ov-tilt-x', '0deg');
        crown.style.setProperty('--ov-tilt-y', '0deg');
      });
    });
  }

  const WISH_KEY = 'orovia_wishlist';

  function wishList() {
    try { return JSON.parse(localStorage.getItem(WISH_KEY) || '[]'); } catch { return []; }
  }

  function saveWishList(list) {
    localStorage.setItem(WISH_KEY, JSON.stringify([...new Set(list.filter(Boolean))]));
    syncWishlistUI();
  }

  function migrateWishList() {
    const next = wishList().map(id => {
      if (!id) return null;
      if (!/^\d+$/.test(String(id))) return id;
      return getCatalog().find(p => String(p.variantId) === String(id))?.slug || null;
    }).filter(Boolean);
    localStorage.setItem(WISH_KEY, JSON.stringify([...new Set(next)]));
  }

  function resolveProductSlug(root) {
    const page = (root && (root.matches?.('[data-ov-pdp-page]') ? root : qs('[data-ov-pdp-page]', root))) || qs('[data-ov-pdp-page]');
    const fromData = page?.dataset?.productSlug?.trim();
    if (fromData && !/^\d+$/.test(fromData)) return fromData;
    const form = page ? qs('[data-ov-demo-form]', page) : qs('[data-ov-demo-form]');
    const prop = form?.querySelector('[name="properties[_slug]"]')?.value?.trim();
    if (prop) return prop;
    const id = form?.querySelector('[name="id"]')?.value?.trim() || fromData || '';
    const byVariant = getCatalog().find(p => String(p.variantId) === String(id));
    if (byVariant) return byVariant.slug;
    const bySlug = getCatalog().find(p => p.slug === id);
    return bySlug?.slug || '';
  }

  function toggleWishlist(slug) {
    if (!slug) return false;
    const list = wishList();
    const i = list.indexOf(slug);
    if (i >= 0) {
      list.splice(i, 1);
      saveWishList(list);
      return false;
    }
    list.push(slug);
    saveWishList(list);
    return true;
  }

  function syncWishlistUI(root = document) {
    const list = wishList();
    const count = list.length;
    qsa('[data-ov-wish-count]', root).forEach(el => {
      el.textContent = count;
      el.hidden = count < 1;
    });
    qsa('[data-ov-wishlist]', root).forEach(btn => {
      const page = btn.closest('[data-ov-pdp-page]');
      const slug = btn.dataset.slug || resolveProductSlug(page || btn);
      const on = slug && list.includes(slug);
      btn.classList.toggle('is-saved', !!on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = on ? 'Saved to wishlist' : 'Add to wishlist';
      if (btn.dataset.ovWishlistLabel !== 'keep') btn.textContent = label;
    });
  }

  window.ovWishlist = { list: wishList, toggle: toggleWishlist, sync: syncWishlistUI, resolve: resolveProductSlug };

  function getCatalog() {
    try { return JSON.parse(qs('#OroviaCatalogMeta')?.textContent || '[]'); } catch { return []; }
  }

  function productFamily(p) {
    const t = String(p?.productType || p?.subtitle || p?.category || '').toLowerCase();
    if (t.includes('bracelet')) return 'bracelet';
    if (t.includes('ear ring') || t.includes('earring')) return 'earring';
    if (t.includes('ring')) return 'ring';
    if (t.includes('locket')) return 'set';
    if (t.includes('chain')) return 'chain';
    if (p?.category === 'bracelet') return 'bracelet';
    if (p?.category === 'earring') return 'earring';
    if (p?.category === 'ring') return 'ring';
    if (p?.category === 'necklace') return 'set';
    return 'jewelry';
  }

  const COMPLETE_LOOK_MATCH = {
    bracelet: ['earring', 'set', 'ring', 'chain'],
    ring: ['set', 'earring', 'bracelet', 'chain'],
    earring: ['bracelet', 'set', 'ring', 'chain'],
    set: ['earring', 'ring', 'bracelet', 'chain'],
    chain: ['set', 'earring', 'ring', 'bracelet'],
    jewelry: ['set', 'earring', 'ring', 'bracelet']
  };

  function getCompleteLook(slug, limit = 4) {
    const cat = getCatalog();
    const current = cat.find(x => x.slug === slug);
    if (!current) return [];
    const family = productFamily(current);
    const preferred = COMPLETE_LOOK_MATCH[family] || COMPLETE_LOOK_MATCH.jewelry;
    const pool = cat.filter(x => x.slug !== slug);
    const scored = pool.map(p => {
      const pf = productFamily(p);
      let score = 0;
      const prefIdx = preferred.indexOf(pf);
      if (prefIdx >= 0) score += (preferred.length - prefIdx) * 120;
      const cp = Number(current.priceRaw) || 0;
      const pp = Number(p.priceRaw) || 0;
      if (cp && pp) {
        const diff = Math.abs(cp - pp) / cp;
        if (diff <= 0.25) score += 45;
        else if (diff <= 0.5) score += 25;
        else if (diff <= 0.85) score += 10;
      }
      if (p.available !== false) score += 15;
      if ((p.images || []).length > 1) score += 8;
      return { p, score, family: pf };
    }).sort((a, b) => b.score - a.score);

    const picked = [];
    const famCount = {};
    for (const item of scored) {
      if (picked.length >= limit) break;
      const count = famCount[item.family] || 0;
      if (count >= 2 && item.score < 200) continue;
      picked.push(item.p);
      famCount[item.family] = count + 1;
    }
    if (picked.length < limit) {
      for (const item of scored) {
        if (picked.length >= limit) break;
        if (picked.some(x => x.slug === item.p.slug)) continue;
        picked.push(item.p);
      }
    }
    return picked.slice(0, limit);
  }

  function buildRailCell(p) {
    const href = p.url || ('/products/' + encodeURIComponent(p.slug));
    const title = escapeHtml(displayTitle(p.title));
    return `<div class="ov-rail__cell" data-ov-rail-cell>
      <article class="ov-prod ov-prod--compact">
        <a href="${escapeHtml(href)}" class="ov-prod__link" data-ov-pdp-link="${escapeHtml(p.slug)}">
          <div class="ov-prod__media">
            <img src="${escapeHtml(p.img)}" alt="${title}" class="ov-prod__img" loading="lazy" width="480" height="480">
            <img src="${escapeHtml(p.imgAlt || p.img)}" alt="" class="ov-prod__img ov-prod__img--alt" loading="lazy" width="480" height="480" aria-hidden="true">
            <span class="ov-prod__view">View piece</span>
          </div>
          <div class="ov-prod__info">
            <h3 class="ov-prod__title">${title}</h3>
            <p class="ov-prod__sub">${escapeHtml(p.subtitle || '')}</p>
            <p class="ov-prod__price">${escapeHtml(p.price)}</p>
          </div>
        </a>
      </article>
    </div>`;
  }

  function hydrateCompleteLookRail(page) {
    const rail = qs('[data-ov-rail]', page);
    const track = qs('[data-ov-complete-look-track], [data-ov-rail-track]', rail);
    if (!rail || !track) return false;
    const slug = page.dataset.productSlug || resolveProductSlug(page);
    const picks = getCompleteLook(slug, 4);
    if (!picks.length) return false;
    track.innerHTML = picks.map(buildRailCell).join('');
    const totalEl = qs('.ov-rail__total', rail);
    if (totalEl) totalEl.textContent = String(picks.length).padStart(2, '0');
    delete rail.dataset.ovRailReady;
    // Fresh rail listeners after HTML replace
    qsa('[data-ov-rail-cell] a', track).forEach(a => {
      a.setAttribute('href', '#' + (a.getAttribute('data-ov-pdp-link') || ''));
    });
    return true;
  }

  function formatMoney(cents) {
    const n = Math.max(0, Number(cents) || 0);
    const amount = n / 100;
    const fmt = window.theme?.settings?.moneyFormat;
    if (fmt && typeof fmt === 'string') {
      const withDec = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const noDec = amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      return fmt
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDec.replace(/,/g, 'X').replace(/\./g, ',').replace(/X/g, '.'))
        .replace(/\{\{\s*amount\s*\}\}/g, withDec);
    }
    return 'Rs ' + amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  /** Amounts in theme settings are PKR whole rupees; Shopify cart money is in cents. */
  function shippingConfig() {
    const s = window.theme?.settings || {};
    const feeRs = Math.max(0, Number(s.shippingFee ?? 250) || 250);
    const thresholdRs = Math.max(0, Number(s.freeShippingThreshold ?? 5000) || 5000);
    return { fee: feeRs * 100, threshold: thresholdRs * 100, feeRs, thresholdRs };
  }

  function scrollPdpToTop(smooth = true) {
    const behavior = smooth ? 'smooth' : 'auto';
    const overlay = qs('[data-ov-pdp-global]');
    const sheet = qs('.ov-pdp-global__sheet', overlay) || overlay;
    if (overlay?.classList.contains('is-open') && sheet) {
      sheet.scrollTo({ top: 0, behavior });
    }
    window.scrollTo({ top: 0, behavior });
  }

  function cartRoutes() {
    const r = window.theme?.routes || {};
    return {
      add: r.cart_add_url || '/cart/add.js',
      change: r.cart_change_url || '/cart/change.js',
      cart: r.cart_url ? `${r.cart_url}.js` : '/cart.js',
      checkout: '/checkout'
    };
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
  }

  function scrollToCenter(viewport, el, smooth = true) {
    if (!viewport || !el) return;
    const vp = viewport.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const delta = (r.left + r.width / 2) - (vp.left + vp.width / 2);
    viewport.scrollTo({ left: viewport.scrollLeft + delta, behavior: smooth ? 'smooth' : 'auto' });
  }

  function resolvePDPRoot(root) {
    if (!root) return null;
    if (root.matches?.('[data-ov-pdp-page]')) return root;
    return qs('[data-ov-pdp-page]', root) || root;
  }

  function resolvePDPMount(root) {
    if (!root) return null;
    if (root.matches?.('[data-ov-pdp-page]')) return root.parentElement || root;
    if (root.querySelector?.('[data-ov-pdp-page]')) return root;
    return root.parentElement || root;
  }

  /* ---- Hero — YouTube sequence (20s per video) ---- */
  function initHeroSlider() {
    const root = qs('[data-ov-hero-slider]');
    if (!root) return;

    const slides = qsa('[data-ov-hero-slide]', root);
    if (!slides.length) return;

    const progress = qs('[data-ov-hero-progress]', root);
    let current = 0;
    let timer;
    const DURATION = 20000;

    const ytUrl = id => {
      const origin = typeof location !== 'undefined' ? location.origin : '';
      const p = new URLSearchParams({
        autoplay: '1', mute: '1', loop: '1', playlist: id,
        controls: '0', rel: '0', playsinline: '1', modestbranding: '1',
        iv_load_policy: '3', disablekb: '1', fs: '0', cc_load_policy: '0',
        enablejsapi: '0', autohide: '1', showinfo: '0', annotations: '0',
        widget_referrer: origin, origin
      });
      return `https://www.youtube-nocookie.com/embed/${id}?${p}`;
    };

    const vimeoUrl = id => {
      const p = new URLSearchParams({
        autoplay: '1', muted: '1', loop: '1', background: '1',
        title: '0', byline: '0', portrait: '0', controls: '0'
      });
      return `https://player.vimeo.com/video/${id}?${p}`;
    };

    const loadSlide = slide => {
      const poster = qs('.ov-hero-slider__poster', slide);
      const ytWrap = qs('[data-ov-hero-yt]', slide);
      if (ytWrap?.dataset.youtubeId) {
        const id = ytWrap.dataset.youtubeId;
        if (poster) poster.style.opacity = '0';
        ytWrap.innerHTML = `<iframe title="" src="${ytUrl(id)}" tabindex="-1" allow="autoplay; encrypted-media" loading="lazy"></iframe>`;
        return;
      }
      if (ytWrap?.dataset.vimeoId) {
        const id = ytWrap.dataset.vimeoId;
        if (poster) poster.style.opacity = '0';
        ytWrap.innerHTML = `<iframe title="" src="${vimeoUrl(id)}" tabindex="-1" allow="autoplay; encrypted-media" loading="lazy"></iframe>`;
        return;
      }
      const v = qs('video', slide);
      if (v) {
        if (!v.src && v.dataset.src) { v.src = v.dataset.src; v.load(); }
        v.controls = false;
        v.removeAttribute('controls');
        v.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback');
        v.disablePictureInPicture = true;
        v.muted = true;
        v.playsInline = true;
        v.play().catch(() => {});
        if (poster) poster.style.opacity = '0';
      }
    };

    const clearSlide = slide => {
      const ytWrap = qs('[data-ov-hero-yt]', slide);
      if (ytWrap) ytWrap.innerHTML = '';
      qs('video', slide)?.pause();
      const poster = qs('.ov-hero-slider__poster', slide);
      if (poster) poster.style.opacity = '1';
    };

    const go = idx => {
      clearSlide(slides[current]);
      slides[current]?.classList.remove('is-active');
      current = (idx + slides.length) % slides.length;
      slides[current].classList.add('is-active');
      loadSlide(slides[current]);
      if (progress) {
        progress.style.transition = 'none';
        progress.style.width = '0%';
        requestAnimationFrame(() => {
          progress.style.transition = `width ${DURATION}ms linear`;
          progress.style.width = '100%';
        });
      }
      clearTimeout(timer);
      if (slides.length > 1) timer = setTimeout(() => go(current + 1), DURATION);
    };

    if (slides.length > 1) {
      let sx = 0, sy = 0;
      root.addEventListener('touchstart', e => {
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      }, { passive: true });
      root.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - sx;
        const dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) < 44 || Math.abs(dx) < Math.abs(dy)) return;
        clearTimeout(timer);
        go(current + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    go(0);
  }

  /* ---- Cart (Shopify Cart API + real checkout) ---- */
  class Cart {
    constructor() {
      this.el = qs('[data-ov-cart]');
      this.checkoutEl = qs('[data-ov-checkout]');
      this.cart = { items: [], item_count: 0, total_price: 0, items_subtotal_price: 0 };
      try { localStorage.removeItem('orovia_demo_cart'); } catch (_) {}
      document.addEventListener('click', e => {
        if (e.target.closest('[data-ov-cart-open]')) {
          e.preventDefault();
          this.open();
        }
      });
      if (this.el) {
        qsa('[data-ov-cart-close], [data-ov-cart-backdrop]', this.el).forEach(b => b.addEventListener('click', () => this.close()));
        qs('[data-ov-cart-body]', this.el)?.addEventListener('click', e => this.onDrawerClick(e));
        qs('[data-ov-cart-checkout]', this.el)?.addEventListener('click', e => {
          e.preventDefault();
          this.openCheckout();
        });
        this.el.addEventListener('change', e => {
          if (e.target?.matches?.('[data-ov-pay-method]')) this.setPayMethod(e.target.value);
        });
      }
      qs('[data-ov-cart-page-mount]')?.addEventListener('click', e => {
        if (e.target.closest('[data-ov-cart-page-checkout]')) {
          e.preventDefault();
          this.openCheckout();
          return;
        }
        this.onDrawerClick(e);
      });
      this.bindCheckout();
      this.refresh().catch(() => {});
    }

    bindCheckout() {
      const root = this.checkoutEl;
      if (!root || root.dataset.ovCheckoutBound) return;
      root.dataset.ovCheckoutBound = '1';
      qsa('[data-ov-checkout-close]', root).forEach(b => b.addEventListener('click', () => this.closeCheckout()));
      root.addEventListener('change', e => {
        if (e.target?.matches?.('[data-ov-pay-method]')) {
          this.setPayMethod(e.target.value);
          this.syncCheckoutSubmitLabel();
        }
      });
      qs('[data-ov-checkout-form]', root)?.addEventListener('submit', e => {
        e.preventDefault();
        this.submitCheckout();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && root.classList.contains('is-open')) this.closeCheckout();
      });
    }

    async refresh() {
      try {
        const res = await fetch(cartRoutes().cart, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('cart');
        this.cart = await res.json();
      } catch (_) {
        this.cart = { items: [], item_count: 0, total_price: 0, items_subtotal_price: 0 };
      }
      this.syncCounts();
      this.renderDrawer();
      this.renderCartPage();
      return this.cart;
    }

    getItems() {
      return (this.cart?.items || []).map(item => {
        const slug = item.handle || item.properties?._slug || '';
        const cat = getCatalog().find(x => x.slug === slug || String(x.variantId) === String(item.variant_id));
        return {
          key: item.key,
          variantId: item.variant_id,
          slug: slug || cat?.slug || '',
          qty: item.quantity,
          title: displayTitle(item.product_title || item.title || cat?.title || 'Orovia Piece'),
          subtitle: item.variant_title && item.variant_title !== 'Default Title'
            ? item.variant_title
            : (cat?.subtitle || item.product_type || ''),
          price: formatMoney(item.final_price ?? item.price),
          priceRaw: item.final_price ?? item.price,
          lineRaw: item.final_line_price ?? item.line_price,
          img: item.image || item.featured_image?.url || cat?.img || cat?.images?.[0] || '',
          url: item.url || (slug ? `#${slug}` : '/cart')
        };
      });
    }

    subtotal() {
      return this.cart?.items_subtotal_price ?? this.cart?.total_price ?? 0;
    }

    shippingFee() {
      const sub = this.subtotal();
      if (sub <= 0) return 0;
      const { fee, threshold } = shippingConfig();
      return sub >= threshold ? 0 : fee;
    }

    isFreeShipping() {
      return this.subtotal() > 0 && this.shippingFee() === 0;
    }

    grandTotal() {
      return this.subtotal() + this.shippingFee();
    }

    shippingLabel() {
      const fee = this.shippingFee();
      return fee === 0 ? 'Free' : formatMoney(fee);
    }

    shippingNote() {
      const { threshold } = shippingConfig();
      const sub = this.subtotal();
      if (sub <= 0) return '';
      if (sub >= threshold) return 'Free shipping unlocked';
      return `Add ${formatMoney(threshold - sub)} more for free shipping`;
    }

    syncShippingUI(root = document) {
      const shipEl = qs('[data-ov-cart-shipping]', root);
      const noteEl = qs('[data-ov-cart-ship-note]', root);
      const totalEl = qs('[data-ov-cart-total]', root);
      if (shipEl) shipEl.textContent = this.shippingLabel();
      if (noteEl) {
        noteEl.textContent = this.shippingNote();
        noteEl.hidden = !this.subtotal();
        noteEl.classList.toggle('is-free', this.isFreeShipping());
      }
      if (totalEl) totalEl.textContent = formatMoney(this.grandTotal());
    }

    syncCounts() {
      const total = this.cart?.item_count || 0;
      qsa('[data-ov-cart-count]').forEach(el => {
        el.textContent = total;
        el.hidden = total < 1;
      });
      qsa('[data-ov-mapp-cart-count]').forEach(el => {
        el.textContent = total;
        el.hidden = total < 1;
      });
    }

    lineHTML(item) {
      const href = item.slug ? `#${escapeHtml(item.slug)}` : escapeHtml(item.url || '/cart');
      const img = item.img
        ? `<img src="${escapeHtml(item.img)}" alt="${escapeHtml(item.title)}" loading="lazy" width="96" height="120">`
        : `<span class="ov-cart-line__ph" aria-hidden="true"></span>`;
      return `<article class="ov-cart-line" data-ov-cart-line="${escapeHtml(item.key)}">
        <a href="${href}" class="ov-cart-line__media" ${item.slug ? `data-ov-pdp-link="${escapeHtml(item.slug)}"` : ''}>${img}</a>
        <div class="ov-cart-line__info">
          <div class="ov-cart-line__row">
            <a href="${href}" class="ov-cart-line__title" ${item.slug ? `data-ov-pdp-link="${escapeHtml(item.slug)}"` : ''}>${escapeHtml(item.title)}</a>
            <p class="ov-cart-line__total">${formatMoney(item.lineRaw || 0)}</p>
          </div>
          ${item.subtitle ? `<p class="ov-cart-line__meta">${escapeHtml(item.subtitle)}</p>` : ''}
          <div class="ov-cart-line__actions">
            <div class="ov-cart-line__qty-wrap">
              <button type="button" class="ov-cart-line__qty-btn" data-ov-cart-qty-minus data-key="${escapeHtml(item.key)}" aria-label="Decrease quantity">−</button>
              <span class="ov-cart-line__qty" data-ov-cart-qty>${item.qty}</span>
              <button type="button" class="ov-cart-line__qty-btn" data-ov-cart-qty-plus data-key="${escapeHtml(item.key)}" aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="ov-cart-line__remove" data-ov-cart-remove data-key="${escapeHtml(item.key)}">Remove</button>
          </div>
        </div>
      </article>`;
    }

    renderDrawer() {
      if (!this.el) return;
      const items = this.getItems();
      const body = qs('[data-ov-cart-body]', this.el);
      const foot = qs('[data-ov-cart-foot]', this.el);
      const subtotalEl = qs('[data-ov-cart-subtotal]', this.el);
      if (!body) return;

      if (!items.length) {
        this.showEmpty();
        return;
      }

      body.innerHTML = items.map(i => this.lineHTML(i)).join('');
      if (subtotalEl) subtotalEl.textContent = formatMoney(this.subtotal());
      if (foot) {
        foot.hidden = false;
        foot.removeAttribute('hidden');
        this.syncShippingUI(foot);
      }
      this.syncPayUI();
    }

    renderCartPage() {
      const mount = qs('[data-ov-cart-page-mount]');
      if (!mount) return;
      const items = this.getItems();

      if (!items.length) {
        mount.innerHTML = `<div class="ov-cart__empty ov-cart__empty--page" data-ov-cart-page-empty>
          <h3>Your bag is empty</h3>
          <p>Discover the Orovia collection.</p>
          <a href="/collections/all" class="ov-btn ov-btn--fill ov-btn--full">Shop collection</a>
        </div>`;
        return;
      }

      mount.innerHTML = `<div class="ov-cart-page__grid">
        <div class="ov-cart-page__items">${items.map(i => this.lineHTML(i)).join('')}</div>
        <aside class="ov-cart-page__summary">
          <div class="ov-cart-page__summary-row"><span>Subtotal</span><span data-ov-cart-page-subtotal>${formatMoney(this.subtotal())}</span></div>
          <div class="ov-cart-page__summary-row"><span>Shipping</span><span data-ov-cart-shipping>${this.shippingLabel()}</span></div>
          <p class="ov-cart__ship-note${this.isFreeShipping() ? ' is-free' : ''}" data-ov-cart-ship-note>${this.shippingNote()}</p>
          <div class="ov-cart-pay ov-cart-page__pay" data-ov-cart-pay>
            <p class="ov-cart-pay__label">Payment</p>
            <div class="ov-cart-pay__opts">
              <label class="ov-cart-pay__opt">
                <input type="radio" name="ov-pay-method-page" value="cod" data-ov-pay-method ${this.payMethod() === 'cod' ? 'checked' : ''}>
                <span><strong>Cash on Delivery</strong><span>Pay in cash when your order arrives.</span></span>
              </label>
              <label class="ov-cart-pay__opt">
                <input type="radio" name="ov-pay-method-page" value="online" data-ov-pay-method ${this.payMethod() === 'online' ? 'checked' : ''}>
                <span><strong>Pay online</strong><span>Card or available methods at checkout.</span></span>
              </label>
            </div>
          </div>
          <div class="ov-cart-page__summary-total"><span>Total</span><span data-ov-cart-total>${formatMoney(this.grandTotal())}</span></div>
          <a href="/checkout" class="ov-btn ov-btn--gold ov-btn--full" data-ov-cart-page-checkout>Checkout</a>
          <a href="/collections/all" class="ov-btn ov-btn--outline ov-btn--full">Continue shopping</a>
        </aside>
      </div>`;
      mount.querySelectorAll('[data-ov-pay-method]').forEach(input => {
        input.addEventListener('change', () => this.setPayMethod(input.value));
      });
    }

    onDrawerClick(e) {
      const minus = e.target.closest('[data-ov-cart-qty-minus]');
      const plus = e.target.closest('[data-ov-cart-qty-plus]');
      const remove = e.target.closest('[data-ov-cart-remove]');
      if (minus) {
        e.preventDefault();
        this.changeQty(minus.dataset.key, -1);
      } else if (plus) {
        e.preventDefault();
        this.changeQty(plus.dataset.key, 1);
      } else if (remove) {
        e.preventDefault();
        this.removeItem(remove.dataset.key);
      }
    }

    async changeQty(key, delta) {
      const item = (this.cart?.items || []).find(i => i.key === key);
      if (!item) return;
      const quantity = Math.max(0, (Number(item.quantity) || 1) + delta);
      await this.updateLine(key, quantity);
    }

    async removeItem(key) {
      await this.updateLine(key, 0);
      this.toast('Removed from bag');
    }

    async updateLine(key, quantity) {
      try {
        const res = await fetch(cartRoutes().change, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!res.ok) throw new Error('change');
        this.cart = await res.json();
        this.syncCounts();
        this.renderDrawer();
        this.renderCartPage();
      } catch (_) {
        this.toast('Could not update bag');
        await this.refresh();
      }
    }

    open() {
      if (!this.el) return;
      const zoom = qs('[data-ov-zoom]');
      if (zoom) {
        zoom.classList.remove('is-open');
        zoom.setAttribute('aria-hidden', 'true');
      }
      // Keep cart above PDP overlay in DOM + stacking
      if (this.el.parentElement !== document.body) document.body.appendChild(this.el);
      else document.body.appendChild(this.el);
      this.renderDrawer();
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ov-lock', 'ov-overlay-open');
      document.documentElement.classList.add('ov-lock');
      this.refresh().catch(() => {});
    }

    close() {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ov-overlay-open');
      const pdpOpen = qs('[data-ov-pdp-global].is-open');
      const searchOpen = qs('[data-ov-search].is-open');
      const navOpen = qs('[data-ov-nav].is-open');
      if (!pdpOpen && !searchOpen && !navOpen) {
        document.body.classList.remove('ov-lock');
        document.documentElement.classList.remove('ov-lock');
      }
    }

    showEmpty() {
      if (!this.el) return;
      const body = qs('[data-ov-cart-body]', this.el);
      const foot = qs('[data-ov-cart-foot]', this.el);
      const subtotalEl = qs('[data-ov-cart-subtotal]', this.el);
      if (body) {
        body.innerHTML = '<p class="ov-cart__empty">Your bag is empty.<br><br><a href="/collections/all" class="ov-link-arrow">Discover Collection →</a></p>';
      }
      if (subtotalEl) subtotalEl.textContent = formatMoney(0);
      if (foot) {
        foot.hidden = true;
        foot.setAttribute('hidden', '');
        this.syncShippingUI(foot);
      }
    }

    async addDemo(slugOrVariantId, q = 1) {
      return this.addItem(slugOrVariantId, q);
    }

    async addItem(slugOrVariantId, q = 1) {
      const qty = Math.max(1, Number(q) || 1);
      let variantId = Number(slugOrVariantId);
      let slug = '';
      if (!variantId) {
        const cat = getCatalog().find(x => x.slug === slugOrVariantId);
        variantId = Number(cat?.variantId);
        slug = cat?.slug || String(slugOrVariantId || '');
      } else {
        const cat = getCatalog().find(x => String(x.variantId) === String(variantId));
        slug = cat?.slug || '';
      }
      if (!variantId) {
        this.toast('This piece is unavailable');
        return;
      }
      try {
        const res = await fetch(cartRoutes().add, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: qty,
              properties: slug ? { _slug: slug } : undefined
            }]
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.description || data?.message || 'add failed');
        await this.refresh();
        this.toast('Added to bag');
        this.open();
      } catch (err) {
        this.toast(err?.message || 'Could not add to bag');
      }
    }

    count() {
      this.syncCounts();
    }

    openCheckout() {
      if (!(this.cart?.item_count > 0)) {
        this.toast('Your bag is empty');
        return;
      }
      if (!this.checkoutEl) {
        this.goCheckout();
        return;
      }
      this.close();
      this.renderCheckout();
      this.syncPayUI();
      this.syncCheckoutSubmitLabel();
      this.checkoutEl.classList.add('is-open');
      this.checkoutEl.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ov-lock', 'ov-overlay-open');
      document.documentElement.classList.add('ov-lock');
      requestAnimationFrame(() => {
        qs('[data-ov-co-email]', this.checkoutEl)?.focus();
      });
    }

    closeCheckout() {
      if (!this.checkoutEl) return;
      this.checkoutEl.classList.remove('is-open');
      this.checkoutEl.setAttribute('aria-hidden', 'true');
      const cartOpen = this.el?.classList.contains('is-open');
      const searchOpen = qs('[data-ov-search].is-open');
      const pdpOpen = qs('[data-ov-pdp-global].is-open');
      if (!cartOpen && !searchOpen && !pdpOpen) {
        document.body.classList.remove('ov-lock', 'ov-overlay-open');
        document.documentElement.classList.remove('ov-lock');
      }
    }

    renderCheckout() {
      const root = this.checkoutEl;
      if (!root) return;
      const items = this.getItems();
      const itemsEl = qs('[data-ov-checkout-items]', root);
      const subEl = qs('[data-ov-checkout-subtotal]', root);
      const shipEl = qs('[data-ov-checkout-shipping]', root);
      const noteEl = qs('[data-ov-checkout-ship-note]', root);
      const totalEl = qs('[data-ov-checkout-total]', root);
      if (itemsEl) {
        itemsEl.innerHTML = items.map(item => `
          <article class="ov-checkout__item">
            <div class="ov-checkout__item-media">
              ${item.img ? `<img src="${escapeHtml(item.img)}" alt="${escapeHtml(item.title)}" loading="lazy" width="64" height="80">` : ''}
              <span class="ov-checkout__item-qty">${item.qty}</span>
            </div>
            <div>
              <p class="ov-checkout__item-title">${escapeHtml(item.title)}</p>
              ${item.subtitle ? `<p class="ov-checkout__item-meta">${escapeHtml(item.subtitle)}</p>` : ''}
            </div>
            <p class="ov-checkout__item-price">${formatMoney(item.lineRaw || 0)}</p>
          </article>
        `).join('');
      }
      if (subEl) subEl.textContent = formatMoney(this.subtotal());
      if (shipEl) shipEl.textContent = this.shippingLabel();
      if (noteEl) {
        noteEl.textContent = this.shippingNote();
        noteEl.hidden = !this.subtotal();
        noteEl.classList.toggle('is-free', this.isFreeShipping());
      }
      if (totalEl) totalEl.textContent = formatMoney(this.grandTotal());
      this.loadCheckoutDraft();
    }

    syncCheckoutSubmitLabel() {
      const btn = qs('[data-ov-checkout-submit]', this.checkoutEl);
      if (!btn) return;
      btn.textContent = this.payMethod() === 'cod'
        ? 'Confirm Cash on Delivery'
        : 'Continue to secure payment';
    }

    loadCheckoutDraft() {
      let draft = null;
      try { draft = JSON.parse(localStorage.getItem('orovia_checkout_draft') || 'null'); } catch (_) {}
      if (!draft || !this.checkoutEl) return;
      const map = [
        ['email', 'data-ov-co-email'],
        ['phone', 'data-ov-co-phone'],
        ['first_name', 'data-ov-co-first'],
        ['last_name', 'data-ov-co-last'],
        ['address1', 'data-ov-co-address'],
        ['address2', 'data-ov-co-address2'],
        ['city', 'data-ov-co-city'],
        ['zip', 'data-ov-co-zip']
      ];
      map.forEach(([key, sel]) => {
        const el = qs(`[${sel}]`, this.checkoutEl);
        if (el && draft[key]) el.value = draft[key];
      });
    }

    readCheckoutForm() {
      const root = this.checkoutEl;
      const val = sel => (qs(sel, root)?.value || '').trim();
      return {
        email: val('[data-ov-co-email]'),
        phone: val('[data-ov-co-phone]'),
        first_name: val('[data-ov-co-first]'),
        last_name: val('[data-ov-co-last]'),
        address1: val('[data-ov-co-address]'),
        address2: val('[data-ov-co-address2]'),
        city: val('[data-ov-co-city]'),
        zip: val('[data-ov-co-zip]'),
        country: val('[data-ov-co-country]') || 'Pakistan'
      };
    }

    validateCheckout(data) {
      if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return 'Please enter a valid email.';
      if (!data.phone || data.phone.replace(/\D/g, '').length < 10) return 'Please enter a valid phone number.';
      if (!data.first_name) return 'First name is required.';
      if (!data.last_name) return 'Last name is required.';
      if (!data.address1) return 'Delivery address is required.';
      if (!data.city) return 'City is required.';
      return '';
    }

    async submitCheckout() {
      if (!(this.cart?.item_count > 0)) {
        this.toast('Your bag is empty');
        return;
      }
      const data = this.readCheckoutForm();
      const errEl = qs('[data-ov-checkout-error]', this.checkoutEl);
      const error = this.validateCheckout(data);
      if (error) {
        if (errEl) {
          errEl.textContent = error;
          errEl.hidden = false;
        }
        this.toast(error);
        return;
      }
      if (errEl) errEl.hidden = true;
      try { localStorage.setItem('orovia_checkout_draft', JSON.stringify(data)); } catch (_) {}

      const btn = qs('[data-ov-checkout-submit]', this.checkoutEl);
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Preparing…';
      }

      const method = this.payMethod();
      const isCod = method === 'cod';
      const freeShip = this.isFreeShipping();
      const shipFee = this.shippingFee();
      const fullName = `${data.first_name} ${data.last_name}`.trim();
      const addressLine = [data.address1, data.address2, data.city, data.zip, data.country].filter(Boolean).join(', ');

      const payload = {
        note: [
          isCod ? 'Cash on Delivery requested — collect payment on delivery.' : 'Customer will pay online at checkout.',
          freeShip ? 'Shipping: Free (order over Rs 5,000).' : `Shipping: flat Rs 250. Estimated ${formatMoney(shipFee)}.`,
          `Deliver to: ${fullName} · ${addressLine} · ${data.phone} · ${data.email}`
        ].join(' '),
        attributes: {
          'Payment method': isCod ? 'Cash on Delivery (COD)' : 'Pay online',
          'COD': isCod ? 'Yes' : 'No',
          'Shipping estimate': freeShip ? 'Free (over Rs 5,000)' : 'Rs 250',
          'Customer name': fullName,
          'Customer email': data.email,
          'Customer phone': data.phone,
          'Delivery address': addressLine,
          'City': data.city
        }
      };

      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (_) {}

      // Prefill Shopify checkout contact where supported
      const params = new URLSearchParams();
      if (data.email) params.set('checkout[email]', data.email);
      if (data.phone) params.set('checkout[shipping_address][phone]', data.phone);
      if (data.first_name) params.set('checkout[shipping_address][first_name]', data.first_name);
      if (data.last_name) params.set('checkout[shipping_address][last_name]', data.last_name);
      if (data.address1) params.set('checkout[shipping_address][address1]', data.address1);
      if (data.address2) params.set('checkout[shipping_address][address2]', data.address2);
      if (data.city) params.set('checkout[shipping_address][city]', data.city);
      if (data.zip) params.set('checkout[shipping_address][zip]', data.zip);
      params.set('checkout[shipping_address][country]', 'Pakistan');
      const qsStr = params.toString();
      window.location.href = qsStr ? `${cartRoutes().checkout}?${qsStr}` : cartRoutes().checkout;
    }

    goCheckout() {
      this.openCheckout();
    }

    payMethod() {
      try {
        return localStorage.getItem('orovia_pay_method') || 'cod';
      } catch (_) {
        return 'cod';
      }
    }

    setPayMethod(value) {
      const next = value === 'online' ? 'online' : 'cod';
      try { localStorage.setItem('orovia_pay_method', next); } catch (_) {}
      qsa('[data-ov-pay-method]').forEach(input => {
        input.checked = input.value === next;
      });
      this.syncCheckoutSubmitLabel();
    }

    syncPayUI() {
      const method = this.payMethod();
      qsa('[data-ov-pay-method]').forEach(input => {
        input.checked = input.value === method;
      });
      this.syncCheckoutSubmitLabel();
    }

    startCheckout() {
      this.openCheckout();
    }

    confirmCheckout() {
      this.submitCheckout();
    }

    toast(msg) {
      let t = qs('[data-ov-toast]');
      if (!t) { t = document.createElement('div'); t.className = 'ov-toast'; t.setAttribute('data-ov-toast', ''); document.body.appendChild(t); }
      t.textContent = msg;
      t.classList.add('is-show');
      setTimeout(() => t.classList.remove('is-show'), 2800);
    }
  }

  function setPageTitle(slug) {
    const cat = getCatalog().find(x => x.slug === slug);
    document.title = cat ? `${displayTitle(cat.title)} — Orovia` : 'Orovia — Fine Jewelry';
  }

  function buildGalleryFilm(p) {
    const tags = ['Portrait', 'Detail', 'Atelier', 'Studio', 'Close', 'Craft', 'View', 'Angle'];
    const imgs = Array.isArray(p.images) && p.images.length
      ? p.images.filter(Boolean)
      : [p.img, p.imgAlt || p.img, p.imgDetail || p.imgAlt || p.img].filter(Boolean);
    const mediaIds = Array.isArray(p.mediaIds) ? p.mediaIds : [];
    const unique = [];
    const seen = new Set();
    imgs.forEach((src, i) => {
      if (!src || seen.has(src)) return;
      seen.add(src);
      unique.push({ src, mediaId: mediaIds[i] != null ? String(mediaIds[i]) : '' });
    });
    const total = String(unique.length).padStart(2, '0');
    const frames = unique.map((item, i) =>
      `<button type="button" class="ov-gfilm__frame${i === 0 ? ' is-active' : ''}" data-ov-gfilm-frame data-pdp-thumb="${escapeHtml(item.src)}"${item.mediaId ? ` data-media-id="${escapeHtml(item.mediaId)}"` : ''} aria-label="${tags[i] || 'View'} view">
        <span class="ov-gfilm__frame-media"><img src="${escapeHtml(item.src)}" alt="" loading="lazy"></span>
        <span class="ov-gfilm__frame-tag">${tags[i] || 'View'}</span>
      </button>`
    ).join('');
    return `<div class="ov-gfilm" data-ov-gfilm>
      <div class="ov-gfilm__head">
        <p class="ov-gfilm__label">Maison Gallery</p>
        <div class="ov-gfilm__counter" aria-live="polite"><span data-ov-gfilm-index>01</span><span class="ov-gfilm__sep"> / </span><span>${total}</span></div>
      </div>
      <div class="ov-gfilm__perforations ov-gfilm__perforations--top" aria-hidden="true"></div>
      <div class="ov-gfilm__stage">
        <button type="button" class="ov-gfilm__nav ov-gfilm__nav--prev" data-ov-gfilm-prev aria-label="Previous image">←</button>
        <div class="ov-gfilm__laser" data-ov-gfilm-laser aria-hidden="true"></div>
        <div class="ov-gfilm__viewport" data-ov-gfilm-viewport tabindex="0" aria-label="Product gallery">
          <div class="ov-gfilm__track" data-ov-gfilm-track>${frames}</div>
        </div>
        <button type="button" class="ov-gfilm__nav ov-gfilm__nav--next" data-ov-gfilm-next aria-label="Next image">→</button>
      </div>
      <div class="ov-gfilm__perforations ov-gfilm__perforations--bottom" aria-hidden="true"></div>
    </div>`;
  }

  function updateMobileAppBar(root, slug) {
    const mapp = qs('[data-ov-mapp]');
    if (!mapp) return;
    /* Desktop uses sheet close (X) only — never arm mobile-open chrome there */
    if (!isMobileView()) {
      hideMobileAppBar();
      return;
    }
    const cat = getCatalog().find(x => x.slug === slug);
    const titleEl = qs('[data-ov-mapp-title]', mapp);
    const priceEl = qs('[data-ov-mapp-price]', mapp);
    const countEl = qs('[data-ov-mapp-cart-count]', mapp);
    if (titleEl) titleEl.textContent = displayTitle(cat?.title) || qs('.ov-pdp-page__title', root)?.textContent?.trim() || 'Product';
    if (priceEl) priceEl.textContent = cat?.price || qs('.ov-pdp-page__price', root)?.textContent?.trim() || '';
    if (countEl) {
      const total = JSON.parse(localStorage.getItem('orovia_demo_cart') || '[]').reduce((s, i) => s + i.qty, 0);
      countEl.textContent = total;
      countEl.hidden = total < 1;
    }
    mapp.classList.add('is-visible');
    mapp.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ov-pdp-mobile-open');
  }

  function hideMobileAppBar() {
    const mapp = qs('[data-ov-mapp]');
    if (!mapp) return;
    mapp.classList.remove('is-visible');
    mapp.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ov-pdp-mobile-open');
  }

  function isMobileView() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function sharePage() {
    const url = location.href;
    if (navigator.share) {
      navigator.share({ title: document.title, url }).catch(() => {});
      return;
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        window.ovCart?.toast('Link copied');
      }).catch(() => {});
    }
  }

  function leaveProductPage() {
    const ref = document.referrer || '';
    const sameOrigin = ref.startsWith(location.origin);
    if (sameOrigin && window.history.length > 1) {
      history.back();
      return;
    }
    location.assign('/collections/all');
  }

  function initMobileAppBar(closePDP) {
    const mapp = qs('[data-ov-mapp]');
    if (!mapp || mapp.dataset.ovMappReady) return;
    mapp.dataset.ovMappReady = '1';

    qs('[data-ov-mapp-back]', mapp)?.addEventListener('click', () => {
      const overlayOpen = qs('[data-ov-pdp-global].is-open');
      if (!overlayOpen && document.body.classList.contains('template-product')) {
        leaveProductPage();
        return;
      }
      closePDP?.();
    });
    qs('[data-ov-mapp-cart]', mapp)?.addEventListener('click', () => window.ovCart?.open());
    qs('[data-ov-mapp-share]', mapp)?.addEventListener('click', sharePage);
    qs('[data-ov-mapp-atc]', mapp)?.addEventListener('click', () => {
      const form =
        qs('[data-ov-pdp-global].is-open [data-ov-demo-form]') ||
        qs('main [data-ov-demo-form]') ||
        qs('[data-ov-demo-form]');
      form?.requestSubmit();
    });
  }

  function initShopifyProductPage() {
    if (!document.body.classList.contains('template-product')) return;
    const page = qs('main [data-ov-pdp-page]') || qs('[data-ov-pdp-page]');
    if (!page) return;

    bindPDP(page);

    const slug = page.dataset.productSlug || '';
    /* Full product URL: show mobile app bar X, or a desktop page close */
    if (isMobileView()) {
      updateMobileAppBar(page, slug);
    } else if (!qs('[data-ov-pdp-page-bar]')) {
      const bar = document.createElement('div');
      bar.className = 'ov-pdp-page-bar';
      bar.setAttribute('data-ov-pdp-page-bar', '');
      bar.innerHTML = `
        <button type="button" class="ov-pdp-page-bag" data-ov-cart-open aria-label="Bag">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M4.5 6.5H13.5L12.5 14.5H5.5L4.5 6.5Z" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/><path d="M7 6.5V5.5C7 4.119 8.119 3 9.5 3C10.881 3 12 4.119 12 5.5V6.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>
          <span class="ov-pdp-page-bag__count" data-ov-cart-count hidden>0</span>
        </button>
        <button type="button" class="ov-pdp-page-close" data-ov-pdp-page-close aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M5 5L13 13M13 5L5 13" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
        </button>`;
      bar.querySelector('[data-ov-pdp-page-close]')?.addEventListener('click', leaveProductPage);
      document.body.appendChild(bar);
      window.ovCart?.syncCounts?.();
    }

    window.addEventListener('resize', () => {
      if (!document.body.classList.contains('template-product')) return;
      if (isMobileView()) {
        updateMobileAppBar(page, slug);
        qs('[data-ov-pdp-page-bar]')?.remove();
      } else {
        hideMobileAppBar();
      }
    });
  }

  function buildVariantOptionsHtml(p) {
    const options = Array.isArray(p.options) ? p.options.filter(o => o && o.values && o.values.length) : [];
    const variants = Array.isArray(p.variants) ? p.variants : [];
    if (!options.length || !variants.length || p.hasOnlyDefaultVariant) return '';
    const isVisualOpt = n => /^(color|colour|metal|finish)$/i.test(String(n || '').trim()) || /gold/i.test(String(n || ''));
    const matchColorImg = (val) => {
      const tokens = (() => {
        const v = String(val || '').toLowerCase();
        if (v.includes('silver') || v.includes('white')) return ['silver', 'white'];
        if (v.includes('rose')) return ['rose'];
        if (v.includes('gold') || v.includes('golden') || v.includes('yellow')) return ['yellowgold', 'golden', 'gold', 'yellow'];
        return [v];
      })();
      const imgs = Array.isArray(p.images) ? p.images : [p.img, p.imgAlt].filter(Boolean);
      const hit = imgs.find(src => {
        const hay = String(src || '').toLowerCase();
        return tokens.some(t => hay.includes(t) && !(t.includes('gold') && hay.includes('silver')));
      });
      return hit || p.img || '';
    };
    const thumbForValue = (oi, val) => {
      const v = variants.find(vr => {
        const opts = Array.isArray(vr.options) ? vr.options : [vr.option1, vr.option2, vr.option3];
        return String(opts[oi] || '') === String(val);
      });
      return v?.featured_image || matchColorImg(val);
    };
    const blocks = options.map((opt, oi) => {
      const selected = opt.selected || opt.values[0];
      const visual = isVisualOpt(opt.name);
      const pills = opt.values.map(val => {
        const thumb = visual ? thumbForValue(oi, val) : '';
        const media = thumb
          ? `<span class="ov-pill__media" aria-hidden="true"><img src="${escapeHtml(thumb)}" alt="" width="56" height="56" loading="lazy"></span>`
          : '';
        return `<button type="button" class="ov-pill${visual ? ' ov-pill--visual' : ''}${val === selected ? ' is-active' : ''}" data-option-value="${escapeHtml(val)}" data-option-index="${oi}" aria-pressed="${val === selected ? 'true' : 'false'}">${media}<span class="ov-pill__label">${escapeHtml(val)}</span></button>`;
      }).join('');
      return `<div class="ov-pdp-page__opt${visual ? ' ov-pdp-page__opt--visual' : ''}" data-ov-option data-option-index="${oi}" data-option-name="${escapeHtml(opt.name)}" data-option-type="${visual ? 'visual' : 'text'}">
        <p class="ov-pdp-page__opt-label">${escapeHtml(opt.name)} — <strong data-selected-value>${escapeHtml(selected)}</strong></p>
        <div class="ov-pdp-page__pills${visual ? ' ov-pdp-page__pills--visual' : ''}" data-ov-option-pills>${pills}</div>
      </div>`;
    }).join('');
    const json = JSON.stringify(variants).replace(/</g, '\\u003c');
    return `${blocks}<script type="application/json" data-ov-variants>${json}</script>`;
  }

  function shipCopy(p, available) {
    if (available === false) return 'Currently unavailable — join the waitlist via Contact';
    if (p && (p.madeToOrder === true || p.madeToOrder === 'true')) {
      return 'Made to order — ships in 15–20 days';
    }
    return 'Ships in 3–7 business days';
  }

  function shippingReturnsCopy(p) {
    if (p && (p.madeToOrder === true || p.madeToOrder === 'true')) {
      return '925 Silver pieces are made to order and ship in 15–20 days. Flat Rs 250 shipping · Free on orders over Rs 5,000. 30-day returns on unworn pieces.';
    }
    return 'Ships in 3–7 business days. Flat Rs 250 shipping · Free on orders over Rs 5,000. 30-day returns on unworn pieces.';
  }

  function buildPDP(p) {
    const rel = getCompleteLook(p.slug, 4);
    const relCells = rel.map(r => buildRailCell(r)).join('');
    const detail = p.imgDetail || p.imgAlt || p.img;
    const title = escapeHtml(displayTitle(p.title));
    const subtitle = escapeHtml((p.subtitle || p.productType || '').trim());
    const sku = (p.sku || '').trim();
    const variantOpts = buildVariantOptionsHtml(p);
    const specsHtml = buildSpecsHtml(p);
    const mto = p.madeToOrder === true || p.madeToOrder === 'true';
    const descBlock = p.descHtml
      ? `<details open><summary>Description</summary><div class="ov-pdp-page__desc">${p.descHtml}</div></details>`
      : (p.desc
        ? `<details open><summary>Description</summary><div class="ov-pdp-page__desc"><p>${escapeHtml(p.desc)}</p></div></details>`
        : '');
    const sizeCta = p.category === 'ring'
      ? `<p class="ov-pdp-page__size-cta"><a class="ov-size-guide-link" href="/pages/size-guide" data-ov-size-guide-link>Size guide</a></p>`
      : '';
    const sizeDetails = p.category === 'ring'
      ? `<details><summary>Size Guide</summary><p>Ring sizes follow USA / Canada standard. Complimentary resizing within 30 days.</p><p><a class="ov-size-guide-link" href="/pages/size-guide" data-ov-size-guide-link>Open the full size guide</a></p></details>`
      : `<details><summary>Size Guide</summary><p>Standard fit. Contact concierge for length or fit questions.</p></details>`;
    return `<section class="ov-pdp-page" data-ov-pdp-page data-product-slug="${p.slug}" data-product-type="${escapeHtml(p.productType || p.subtitle || '')}"${mto ? ' data-made-to-order="true"' : ''}>
      <div class="ov-pdp-page__grid">
        <div class="ov-pdp-page__gallery">
          <div class="ov-pdp-page__main" data-pdp-main>
            <button type="button" class="ov-pdp-page__main-nav ov-pdp-page__main-nav--prev" data-pdp-main-prev aria-label="Previous image">←</button>
            <button type="button" class="ov-pdp-page__main-nav ov-pdp-page__main-nav--next" data-pdp-main-next aria-label="Next image">→</button>
            <button type="button" class="ov-pdp-page__zoom-btn" data-pdp-zoom aria-label="Zoom image"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="5.5" stroke="currentColor" stroke-width="1.4"/><path d="M14.2 14.2L19 19" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M10 7.5v5M7.5 10h5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button>
            <div class="ov-pdp-page__main-stage" data-pdp-main-stage><img src="${p.img}" alt="${title}" class="ov-pdp-page__main-img" id="pdpMainImg"></div>
          </div>
          ${buildGalleryFilm(p)}
        </div>
        <div class="ov-pdp-page__buy" data-pdp-panel>
          <nav class="ov-pdp-page__crumb"><a href="/">Home</a> / <a href="/collections/all">Collection</a> / <span>${title}</span></nav>
          <p class="ov-pdp-page__brand">Orovia</p>
          <h1 class="ov-pdp-page__title">${title}</h1>
          ${subtitle ? `<p class="ov-pdp-page__sub">${subtitle}</p>` : ''}
          <p class="ov-pdp-page__price" data-price-wrapper>${p.price}</p>
          ${sku ? `<p class="ov-pdp-page__sku">Ref. ${escapeHtml(sku)}</p>` : ''}
          <form class="ov-pdp-page__form" data-ov-demo-form data-base-price="${p.price}" data-base-raw="${p.priceRaw || 0}" data-product-slug="${p.slug}"><input type="hidden" name="id" value="${p.variantId || p.slug}" data-ov-variant-id><input type="hidden" name="properties[_slug]" value="${p.slug}">
            ${variantOpts}
            <div class="ov-pdp-page__actions">
              <div class="ov-pdp-page__qty"><button type="button" data-qty-minus>−</button><input type="number" name="quantity" value="1" min="1" data-qty-input><button type="button" data-qty-plus>+</button></div>
              <button type="submit" class="ov-btn ov-btn--fill ov-pdp-page__atc" data-ov-atc>${p.available === false ? 'Sold out' : 'Add to bag'}</button>
            </div>
            <div class="ov-pdp-page__secondary">
              <button type="button" class="ov-pdp-page__ghost" data-ov-wishlist data-slug="${p.slug}">Add to wishlist</button>
              <button type="button" class="ov-pdp-page__ghost ov-pdp-page__ghost--share" data-ov-share>Share</button>
            </div>
          </form>
          <p class="ov-pdp-page__ship" data-ov-ship>${shipCopy(p, p.available !== false)}</p>
          ${sizeCta}
          ${specsHtml}
          <div class="ov-pdp-page__acc">
            ${descBlock}
            ${sizeDetails}
            <details><summary>Shipping & Returns</summary><p>${shippingReturnsCopy(p)}</p></details>
            <details><summary>Care</summary><p>Store in provided pouch. Clean with soft cloth.</p></details>
          </div>
        </div>
      </div>
      <div class="ov-pdp-page__craft ov-wrap">
        <div class="ov-pdp-page__craft-media"><img src="${detail}" alt="${title}" loading="lazy"></div>
      </div>
      <div class="ov-rail" data-ov-rail>
        <div class="ov-rail__head ov-wrap">
          <div class="ov-rail__head-copy">
            <p class="ov-rail__eyebrow">Curated for you</p>
            <h2 class="ov-rail__title">Complete the look</h2>
          </div>
          <div class="ov-rail__counter" aria-live="polite">
            <span class="ov-rail__index" data-ov-rail-index>01</span>
            <span class="ov-rail__dash">—</span>
            <span class="ov-rail__total">${String(Math.max(rel.length, 1)).padStart(2, '0')}</span>
          </div>
        </div>
        <div class="ov-rail__stage">
          <div class="ov-rail__glow" aria-hidden="true"></div>
          <div class="ov-rail__viewport" data-ov-rail-viewport tabindex="0" aria-label="Related pieces">
            <div class="ov-rail__track" data-ov-rail-track data-ov-complete-look-track>${relCells}</div>
          </div>
        </div>
        <div class="ov-rail__controls ov-wrap">
          <button type="button" class="ov-rail__btn" data-ov-rail-prev aria-label="Previous piece"><span aria-hidden="true">←</span></button>
          <div class="ov-rail__beam" aria-hidden="true"><span class="ov-rail__beam-fill" data-ov-rail-beam></span></div>
          <button type="button" class="ov-rail__btn" data-ov-rail-next aria-label="Next piece"><span aria-hidden="true">→</span></button>
        </div>
      </div>
    </section>
    <footer class="ov-ft">
      <div class="ov-ft__top">
        <div class="ov-ft__brand">
          <div class="ov-ft__crest" data-ov-ft-crest>
            <div class="ov-ft-crown" data-ov-ft-crown aria-hidden="true">
              <span class="ov-ft-crown__inner">
                <img src="${(window.theme && window.theme.assets && window.theme.assets.crown) || ''}" alt="" class="ov-ft-crown__img" width="96" height="96" loading="lazy" decoding="async">
              </span>
            </div>
            <a href="/" class="ov-ft__crest-logo" aria-label="Orovia home"><span class="ov-brand ov-brand--compact"><img src="${(window.theme && window.theme.assets && window.theme.assets.logo) || ''}" alt="Orovia" class="ov-brand__wordmark ov-brand__wordmark--compact" width="220" height="64" loading="eager"></span></a>
          </div>
          <p class="ov-ft__tag">Treasures of your own.</p>
          <div class="ov-ft__social">
            <a href="https://www.instagram.com/orovia.pk/?hl=en" class="ov-ft__social-link" target="_blank" rel="noopener noreferrer" aria-label="Orovia on Instagram">
              <svg class="ov-ft__social-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" stroke-width="1.4"/><circle cx="12" cy="12" r="4.2" stroke="currentColor" stroke-width="1.4"/><circle cx="17.4" cy="6.6" r="1.1" fill="currentColor"/></svg>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61578444571275" class="ov-ft__social-link" target="_blank" rel="noopener noreferrer" aria-label="Orovia on Facebook">
              <svg class="ov-ft__social-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M14 9h3V6h-3c-1.7 0-3 1.3-3 3v2H9v3h2v7h3v-7h2.2l.8-3H14V9z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>
            </a>
          </div>
        </div>
        <div class="ov-ft__cols">
          <div>
            <p class="ov-ft__head">Shop</p>
            <a href="/collections/all">Collection</a>
            <a href="/pages/rings">Rings</a>
            <a href="/pages/bracelets">Bracelets</a>
            <a href="/pages/bangles">Bangles</a>
            <a href="/pages/earrings">Earrings</a>
            <a href="/pages/necklaces">Necklaces</a>
          </div>
          <div>
            <p class="ov-ft__head">Maison</p>
            <a href="/pages/our-story">Our Story</a>
            <a href="/pages/our-craftsmanship">Craftsmanship</a>
            <a href="/blogs/journal">Journal</a>
          </div>
          <div>
            <p class="ov-ft__head">Client Care</p>
            <a href="/pages/contact">Contact</a>
            <a href="/pages/faq">FAQ</a>
            <a href="/pages/shipping-delivery">Shipping</a>
          </div>
        </div>
      </div>
      <div class="ov-ft__bottom">
        <p>© ${new Date().getFullYear()} Orovia. All rights reserved.</p>
        <div class="ov-ft__legal">
          <a href="/pages/privacy-policy">Privacy</a>
          <a href="/pages/terms-and-conditions">Terms</a>
        </div>
      </div>
    </footer>
    <div class="ov-pdp-bar" data-pdp-bar><span class="ov-pdp-bar__title">${title}</span><span class="ov-pdp-bar__price">${p.price}</span><button type="button" class="ov-btn ov-btn--fill" data-pdp-bar-submit>Add to bag</button></div>`;
  }

  function initVariantPicker(page) {
    const form = qs('[data-ov-demo-form]', page);
    if (!form || form.dataset.ovVariantsReady) return;
    const jsonEl = qs('[data-ov-variants]', form);
    if (!jsonEl) return;

    let variants = [];
    try { variants = JSON.parse(jsonEl.textContent || '[]'); } catch { variants = []; }
    if (!variants.length) return;
    form.dataset.ovVariantsReady = '1';

    const idInput = qs('[data-ov-variant-id], input[name="id"]', form);
    const priceEl = qs('[data-price-wrapper]', page) || qs('.ov-pdp-page__price', page);
    const skuEl = qs('.ov-pdp-page__sku', page);
    const atc = qs('[data-ov-atc], .ov-pdp-page__atc', page);
    const ship = qs('[data-ov-ship]', page);
    const barPrice = qs('[data-pdp-bar] .ov-pdp-bar__price', page.parentElement || document);
    const mainWrap = qs('[data-pdp-main]', page);
    const optionBlocks = qsa('[data-ov-option]', form);

    const selected = () => optionBlocks.map(block => {
      const active = qs('.ov-pill.is-active', block);
      return active?.getAttribute('data-option-value') || qs('[data-selected-value]', block)?.textContent?.trim() || '';
    });

    const findVariant = () => {
      const sel = selected();
      return variants.find(v => {
        const opts = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(x => x != null && x !== '');
        return sel.every((val, i) => String(opts[i] || '') === String(val));
      }) || variants.find(v => v.available) || variants[0];
    };

    const syncUnavailable = () => {
      const sel = selected();
      optionBlocks.forEach((block, oi) => {
        qsa('.ov-pill', block).forEach(pill => {
          const val = pill.getAttribute('data-option-value');
          const trial = sel.map((s, i) => (i === oi ? val : s));
          const match = variants.find(v => {
            const opts = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(x => x != null && x !== '');
            return trial.every((t, i) => String(opts[i] || '') === String(t));
          });
          const bad = !match || match.available === false;
          pill.classList.toggle('is-unavailable', bad);
          pill.setAttribute('aria-disabled', bad ? 'true' : 'false');
        });
      });
    };

    const colorTokens = (val) => {
      const v = String(val || '').toLowerCase();
      if (!v) return [];
      if (v.includes('silver') || v.includes('white')) return ['silver', 'white'];
      if (v.includes('rose')) return ['rose', 'rosegold', 'rose-gold'];
      if (v.includes('gold') || v.includes('golden') || v.includes('yellow')) {
        return ['gold', 'golden', 'yellowgold', 'yellow-gold', 'yellow'];
      }
      return [v];
    };

    const variantColorValue = (variant) => {
      const opts = Array.isArray(variant.options) ? variant.options : [variant.option1, variant.option2, variant.option3];
      const names = optionBlocks.map(b => (b.getAttribute('data-option-name') || '').toLowerCase());
      for (let i = 0; i < names.length; i++) {
        if (/color|colour|metal|finish|gold/.test(names[i])) return opts[i] || '';
      }
      return opts.find(o => /gold|silver|rose|golden/i.test(String(o || ''))) || opts[0] || '';
    };

    const applyVariantImage = (variant) => {
      if (!variant) return;
      const gallery = page._ovGallery;
      if (gallery) {
        if (variant.media_id && gallery.focusMediaId(variant.media_id)) return;
        if (variant.featured_image && gallery.focusSrc(variant.featured_image)) return;
        // No Admin variant image yet — filter gallery by Gold/Silver filename/alt
        const colorVal = variantColorValue(variant);
        if (colorVal && gallery.filterByColor(colorVal)) return;
      }
      if (variant.featured_image && mainWrap) {
        const stage = qs('[data-pdp-main-stage]', mainWrap) || mainWrap;
        let img = qs('.ov-pdp-page__main-img', stage);
        mainWrap.classList.add('is-switching');
        if (!img || img.tagName !== 'IMG') {
          stage.innerHTML = `<img src="${variant.featured_image}" alt="" class="ov-pdp-page__main-img">`;
        } else {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = variant.featured_image;
        }
        setTimeout(() => mainWrap.classList.remove('is-switching'), 280);
      }
    };

    const applyVariant = (variant) => {
      if (!variant) return;
      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = variant.priceMoney || formatMoney(variant.price);
      if (barPrice) barPrice.textContent = variant.priceMoney || formatMoney(variant.price);
      if (skuEl && variant.sku) skuEl.textContent = `Ref. ${variant.sku}`;
      if (atc) {
        atc.disabled = !variant.available;
        atc.textContent = variant.available ? 'Add to bag' : 'Sold out';
      }
      if (ship) {
        const mto = page?.hasAttribute?.('data-made-to-order');
        ship.textContent = variant.available
          ? (mto ? 'Made to order — ships in 15–20 days' : 'Ships in 3–7 business days')
          : 'Currently unavailable — join the waitlist via Contact';
      }
      applyVariantImage(variant);
      form.dataset.baseRaw = String(variant.price || 0);
      form.dataset.basePrice = variant.priceMoney || '';
      syncUnavailable();
    };

    optionBlocks.forEach(block => {
      qsa('.ov-pill', block).forEach(pill => {
        pill.addEventListener('click', () => {
          qsa('.ov-pill', block).forEach(x => {
            x.classList.remove('is-active');
            x.setAttribute('aria-pressed', 'false');
          });
          pill.classList.add('is-active');
          pill.setAttribute('aria-pressed', 'true');
          const label = qs('[data-selected-value]', block);
          if (label) label.textContent = pill.getAttribute('data-option-value') || pill.textContent;
          applyVariant(findVariant());
        });
      });
    });

    applyVariant(findVariant());
  }

  function bindPDP(root) {
    const page = resolvePDPRoot(root);
    const mount = resolvePDPMount(root);
    if (!page) return;

    initGalleryFilm(page);
    initVariantPicker(page);
    hydrateCompleteLookRail(page);
    const form = qs('[data-ov-demo-form]', page);
    form?.addEventListener('submit', e => {
      e.preventDefault();
      window.ovCart?.addDemo(new FormData(form).get('id'), parseInt(new FormData(form).get('quantity') || 1, 10));
    });
    qs('[data-qty-minus]', page)?.addEventListener('click', () => { const i = qs('[data-qty-input]', page); if (i) i.value = Math.max(1, +i.value - 1); });
    qs('[data-qty-plus]', page)?.addEventListener('click', () => { const i = qs('[data-qty-input]', page); if (i) i.value = +i.value + 1; });
    qs('[data-ov-wishlist]', page)?.addEventListener('click', e => {
      e.preventDefault();
      const btn = e.currentTarget;
      const slug = btn.dataset.slug || resolveProductSlug(page);
      if (!slug) {
        window.ovCart?.toast('Could not save this piece');
        return;
      }
      const saved = toggleWishlist(slug);
      syncWishlistUI(page);
      if (saved) {
        window.ovCart?.toast('Saved — opening wishlist');
        setTimeout(() => { window.location.href = '/pages/wishlist'; }, 650);
      } else {
        window.ovCart?.toast('Removed from wishlist');
      }
    });
    syncWishlistUI(page);
    initFooterCrown();
    qs('[data-ov-share]', page)?.addEventListener('click', sharePage);
    const panel = qs('[data-pdp-panel]', page);
    const bar = qs('[data-pdp-bar]', mount || page.parentElement || page);
    if (panel && bar) {
      new IntersectionObserver(([e]) => bar.classList.toggle('is-visible', !e.isIntersecting)).observe(panel);
      qs('[data-pdp-bar-submit]', mount || page.parentElement || page)?.addEventListener('click', () => form?.requestSubmit());
    }
    initRelatedRail(page);
  }

  function ensureZoomOverlay() {
    let el = qs('[data-ov-zoom]');
    if (el) return el;
    el = document.createElement('div');
    el.className = 'ov-zoom';
    el.setAttribute('data-ov-zoom', '');
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = `
      <div class="ov-zoom__bg" data-ov-zoom-close></div>
      <button type="button" class="ov-zoom__close" data-ov-zoom-close aria-label="Close zoom">✕</button>
      <button type="button" class="ov-zoom__nav ov-zoom__nav--prev" data-ov-zoom-prev aria-label="Previous">←</button>
      <button type="button" class="ov-zoom__nav ov-zoom__nav--next" data-ov-zoom-next aria-label="Next">→</button>
      <div class="ov-zoom__stage" data-ov-zoom-stage>
        <img class="ov-zoom__img" data-ov-zoom-img alt="" hidden>
      </div>
      <p class="ov-zoom__hint" data-ov-zoom-hint>Pinch or scroll to zoom · Drag to pan</p>
    `;
    document.body.appendChild(el);

    // Singleton handlers — always talk to the active PDP gallery session
    el._ovZoomSession = null;
    el._ovZoomToken = 0;

    const session = () => el._ovZoomSession;

    qsa('[data-ov-zoom-close]', el).forEach(b => {
      b.addEventListener('click', e => {
        e.preventDefault();
        session()?.close?.();
      });
    });
    qs('[data-ov-zoom-prev]', el)?.addEventListener('click', e => {
      e.preventDefault();
      session()?.prev?.();
    });
    qs('[data-ov-zoom-next]', el)?.addEventListener('click', e => {
      e.preventDefault();
      session()?.next?.();
    });
    document.addEventListener('keydown', e => {
      if (!el.classList.contains('is-open')) return;
      if (e.key === 'Escape') session()?.close?.();
      if (e.key === 'ArrowLeft') session()?.prev?.();
      if (e.key === 'ArrowRight') session()?.next?.();
    });

    // Gestures once on the shared overlay
    const stage = qs('[data-ov-zoom-stage]', el);
    const img = qs('[data-ov-zoom-img]', el);
    let gScale = 1, gX = 0, gY = 0, gDrag = false, gLastX = 0, gLastY = 0, gPinch = 0;
    const applyG = () => {
      if (!img) return;
      img.style.transform = `translate3d(${gX}px, ${gY}px, 0) scale(${gScale})`;
      el.classList.toggle('is-zoomed', gScale > 1.05);
    };
    el._ovZoomGestures = {
      reset: () => { gScale = 1; gX = 0; gY = 0; applyG(); }
    };

    stage?.addEventListener('wheel', e => {
      if (!el.classList.contains('is-open')) return;
      e.preventDefault();
      gScale = Math.min(4, Math.max(1, gScale + (e.deltaY < 0 ? 0.18 : -0.18)));
      if (gScale <= 1) { gX = 0; gY = 0; }
      applyG();
    }, { passive: false });

    img?.addEventListener('dblclick', e => {
      e.preventDefault();
      if (gScale > 1.2) { gScale = 1; gX = 0; gY = 0; }
      else gScale = 2.4;
      applyG();
    });

    stage?.addEventListener('pointerdown', e => {
      if (!el.classList.contains('is-open') || gScale <= 1.05) return;
      gDrag = true;
      gLastX = e.clientX;
      gLastY = e.clientY;
      stage.setPointerCapture?.(e.pointerId);
    });
    stage?.addEventListener('pointermove', e => {
      if (!gDrag) return;
      gX += e.clientX - gLastX;
      gY += e.clientY - gLastY;
      gLastX = e.clientX;
      gLastY = e.clientY;
      applyG();
    });
    const endG = () => { gDrag = false; };
    stage?.addEventListener('pointerup', endG);
    stage?.addEventListener('pointercancel', endG);

    stage?.addEventListener('touchstart', e => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        gPinch = Math.hypot(dx, dy);
      }
    }, { passive: true });
    stage?.addEventListener('touchmove', e => {
      if (e.touches.length !== 2 || !gPinch) return;
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      gScale = Math.min(4, Math.max(1, gScale * (dist / gPinch)));
      gPinch = dist;
      if (gScale <= 1) { gX = 0; gY = 0; }
      applyG();
    }, { passive: false });
    stage?.addEventListener('touchend', () => { gPinch = 0; }, { passive: true });

    let tapX = 0, tapY = 0, tapT = 0;
    img?.addEventListener('touchstart', e => {
      if (e.touches.length !== 1) return;
      tapX = e.touches[0].clientX;
      tapY = e.touches[0].clientY;
      tapT = Date.now();
    }, { passive: true });
    img?.addEventListener('touchend', e => {
      if (e.changedTouches.length !== 1) return;
      const dx = Math.abs(e.changedTouches[0].clientX - tapX);
      const dy = Math.abs(e.changedTouches[0].clientY - tapY);
      if (dx > 12 || dy > 12 || Date.now() - tapT > 280) return;
      if (gScale > 1.2) { gScale = 1; gX = 0; gY = 0; }
      else gScale = 2.2;
      applyG();
    }, { passive: true });

    return el;
  }

  function clearZoomImage(zoomEl) {
    const img = qs('[data-ov-zoom-img]', zoomEl);
    if (!img) return;
    img.hidden = true;
    img.removeAttribute('src');
    img.style.opacity = '0';
    img.style.transform = 'translate3d(0,0,0) scale(1)';
    zoomEl.classList.remove('is-zoomed');
    zoomEl._ovZoomGestures?.reset?.();
  }

  function forceCloseZoom() {
    const zoomEl = qs('[data-ov-zoom]');
    if (!zoomEl) return;
    zoomEl._ovZoomSession?.close?.();
    zoomEl._ovZoomSession = null;
    zoomEl.classList.remove('is-open');
    zoomEl.setAttribute('aria-hidden', 'true');
    clearZoomImage(zoomEl);
  }

  function hiResUrl(src) {
    if (!src) return '';
    try {
      if (src.includes('cdn.shopify.com') || src.includes('/cdn/shop/')) {
        return src
          .replace(/([?&])width=\d+/g, '$1width=2400')
          .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|1024x1024|2048x2048)(\.|_)/, '_2400x$2')
          .replace(/width=\d+/, 'width=2400');
      }
    } catch (_) {}
    return src;
  }

  function initGalleryFilm(root) {
    const gfilm = qs('[data-ov-gfilm]', root);
    if (!gfilm || gfilm.dataset.ovGfilmReady) return;

    const viewport = qs('[data-ov-gfilm-viewport]', gfilm);
    const frames = qsa('[data-ov-gfilm-frame]', gfilm);
    const laser = qs('[data-ov-gfilm-laser]', gfilm);
    const indexEl = qs('[data-ov-gfilm-index]', gfilm);
    const prevBtn = qs('[data-ov-gfilm-prev]', gfilm);
    const nextBtn = qs('[data-ov-gfilm-next]', gfilm);
    const mainWrap = qs('[data-pdp-main]', root);
    if (!viewport || !frames.length || !mainWrap) return;

    // Ensure main controls exist (store HTML / older markup)
    if (!qs('[data-pdp-main-stage]', mainWrap)) {
      const existing = mainWrap.innerHTML;
      mainWrap.innerHTML = `
        <button type="button" class="ov-pdp-page__main-nav ov-pdp-page__main-nav--prev" data-pdp-main-prev aria-label="Previous image">←</button>
        <button type="button" class="ov-pdp-page__main-nav ov-pdp-page__main-nav--next" data-pdp-main-next aria-label="Next image">→</button>
        <button type="button" class="ov-pdp-page__zoom-btn" data-pdp-zoom aria-label="Zoom image"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="5.5" stroke="currentColor" stroke-width="1.4"/><path d="M14.2 14.2L19 19" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M10 7.5v5M7.5 10h5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button>
        <div class="ov-pdp-page__main-stage" data-pdp-main-stage>${existing}</div>
      `;
    }

    const mainStage = qs('[data-pdp-main-stage]', mainWrap) || mainWrap;
    const mainPrev = qs('[data-pdp-main-prev]', mainWrap);
    const mainNext = qs('[data-pdp-main-next]', mainWrap);
    const zoomBtn = qs('[data-pdp-zoom]', mainWrap);

    gfilm.dataset.ovGfilmReady = '1';

    let focus = -1;
    let lockScrollSync = false;
    let lockTimer = 0;
    let downX = 0;
    let downY = 0;
    let downScroll = 0;
    let downFrame = null;

    const pad = n => String(n).padStart(2, '0');
    const syncNav = () => {
      const atStart = focus <= 0;
      const atEnd = focus >= frames.length - 1;
      [prevBtn, mainPrev].forEach(b => { if (b) b.disabled = atStart; });
      [nextBtn, mainNext].forEach(b => { if (b) b.disabled = atEnd; });
    };

    const frameSrc = frame => {
      const video = frame.getAttribute('data-pdp-video')?.trim();
      if (video) return { type: 'video', src: video };
      const thumb = frame.getAttribute('data-pdp-thumb')?.trim() || qs('img', frame)?.getAttribute('src') || '';
      return { type: 'image', src: thumb };
    };

    const isCached = url => {
      if (!url) return false;
      const probe = new Image();
      probe.src = url;
      return probe.complete && probe.naturalWidth > 0;
    };

    /* Prefetch full gallery URLs so main swaps are cache-hits */
    frames.forEach(frame => {
      const media = frameSrc(frame);
      if (media.type !== 'image' || !media.src) return;
      const warm = new Image();
      warm.decoding = 'async';
      warm.src = media.src;
    });

    const setMainImage = (src, frame) => {
      if (!mainWrap || !src) return;
      qs('video', mainStage)?.remove();
      let img = qs('.ov-pdp-page__main-img', mainStage);
      if (!img || img.tagName !== 'IMG') {
        mainStage.innerHTML = '<img alt="" class="ov-pdp-page__main-img" decoding="async">';
        img = qs('.ov-pdp-page__main-img', mainStage);
      }
      // Shopify image_tag leaves srcset — must clear or main image never changes
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.decoding = 'async';

      mainWrap._ovImgToken = (mainWrap._ovImgToken || 0) + 1;
      const token = mainWrap._ovImgToken;

      const commit = url => {
        if (!url) return;
        if (img.getAttribute('src') !== url) img.src = url;
      };

      const thumbEl = frame ? qs('img', frame) : null;
      const preview = (thumbEl?.currentSrc || thumbEl?.src || '').trim();

      // Full size already in cache → instant swap, no fade
      if (isCached(src)) {
        commit(src);
        mainWrap.classList.remove('is-switching');
        return;
      }

      // Show the already-decoded gallery thumb immediately, then upgrade
      if (preview) {
        commit(preview);
        mainWrap.classList.remove('is-switching');
        const upgrade = new Image();
        upgrade.decoding = 'async';
        upgrade.onload = () => {
          if (token !== mainWrap._ovImgToken) return;
          commit(src);
        };
        upgrade.src = src;
        return;
      }

      // No preview — load full with a short soft fade
      mainWrap.classList.add('is-switching');
      commit(src);
      const done = () => {
        if (token !== mainWrap._ovImgToken) return;
        mainWrap.classList.remove('is-switching');
        img.removeEventListener('load', done);
      };
      if (img.complete) done();
      else {
        img.addEventListener('load', done);
        setTimeout(done, 500);
      }
    };

    const setMainVideo = src => {
      if (!mainWrap || !src) return;
      mainWrap.classList.add('is-switching');
      mainStage.innerHTML = `<video src="${src}" autoplay muted loop playsinline class="ov-pdp-page__main-img"></video>`;
      setTimeout(() => mainWrap.classList.remove('is-switching'), 180);
    };

    const applyFrame = frame => {
      const media = frameSrc(frame);
      if (media.type === 'video') setMainVideo(media.src);
      else if (media.src) setMainImage(media.src, frame);
    };

    const moveLaser = () => {
      if (!laser || focus < 0) return;
      const frame = frames[focus];
      const stage = gfilm.querySelector('.ov-gfilm__stage');
      if (!frame || !stage) return;
      const fr = frame.getBoundingClientRect();
      const sr = stage.getBoundingClientRect();
      laser.style.width = `${fr.width}px`;
      laser.style.transform = `translateX(${fr.left - sr.left}px)`;
    };

    const nearestIndex = () => {
      const vp = viewport.getBoundingClientRect();
      const center = vp.left + vp.width / 2;
      let best = 0;
      let bestDist = Infinity;
      frames.forEach((frame, i) => {
        const r = frame.getBoundingClientRect();
        const dist = Math.abs(r.left + r.width / 2 - center);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    };

    const needsScroll = () => viewport.scrollWidth > viewport.clientWidth + 8;

    const setFocus = (i, { scroll = false, smooth = true, apply = true } = {}) => {
      const next = Math.max(0, Math.min(frames.length - 1, i));
      const changed = next !== focus;
      focus = next;
      frames.forEach((f, idx) => {
        const on = idx === focus;
        f.classList.toggle('is-active', on);
        f.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (indexEl) indexEl.textContent = pad(focus + 1);
      if (apply && changed) {
        applyFrame(frames[focus]);
        if (zoomEl?.classList.contains('is-open')) setZoomImage();
      }
      if (scroll && needsScroll()) {
        lockScrollSync = true;
        clearTimeout(lockTimer);
        scrollToCenter(viewport, frames[focus], smooth);
        lockTimer = setTimeout(() => { lockScrollSync = false; }, smooth ? 500 : 120);
      } else if (!needsScroll()) {
        viewport.scrollLeft = 0;
      }
      syncNav();
      requestAnimationFrame(moveLaser);
    };

    const visibleIndexes = () => frames
      .map((f, i) => (!f.hidden && !f.classList.contains('is-color-hidden') ? i : -1))
      .filter(i => i >= 0);

    const step = dir => {
      const vis = visibleIndexes();
      if (!vis.length) {
        setFocus(focus + dir, { scroll: true, smooth: true, apply: true });
        return;
      }
      let pos = vis.indexOf(focus);
      if (pos < 0) pos = dir > 0 ? -1 : 0;
      const nextPos = Math.max(0, Math.min(vis.length - 1, pos + dir));
      setFocus(vis[nextPos], { scroll: true, smooth: true, apply: true });
    };

    prevBtn?.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      step(-1);
    });
    nextBtn?.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      step(1);
    });
    mainPrev?.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      step(-1);
    });
    mainNext?.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      step(1);
    });

    /* ---- Fullscreen zoom (shared overlay, per-product session) ---- */
    const zoomEl = ensureZoomOverlay();
    const zoomImg = qs('[data-ov-zoom-img]', zoomEl);

    const setZoomImage = () => {
      const media = frameSrc(frames[focus]);
      if (!zoomImg || media.type === 'video' || !media.src) return;
      const next = hiResUrl(media.src);
      const token = ++zoomEl._ovZoomToken;
      // Hide previous product image immediately (no flash of old piece)
      clearZoomImage(zoomEl);

      const probe = new Image();
      probe.onload = () => {
        if (token !== zoomEl._ovZoomToken) return;
        zoomImg.src = next;
        zoomImg.hidden = false;
        zoomImg.style.opacity = '1';
        zoomEl._ovZoomGestures?.reset?.();
      };
      probe.onerror = () => {
        if (token !== zoomEl._ovZoomToken) return;
        zoomImg.src = media.src;
        zoomImg.hidden = false;
        zoomImg.style.opacity = '1';
        zoomEl._ovZoomGestures?.reset?.();
      };
      probe.src = next;
    };

    const closeZoom = () => {
      if (zoomEl._ovZoomSession?.root === root) zoomEl._ovZoomSession = null;
      zoomEl.classList.remove('is-open');
      zoomEl.setAttribute('aria-hidden', 'true');
      clearZoomImage(zoomEl);
      const cartOpen = qs('[data-ov-cart].is-open');
      const pdpOpen = qs('[data-ov-pdp-global].is-open');
      const coOpen = qs('[data-ov-checkout].is-open');
      if (!cartOpen && !pdpOpen && !coOpen) {
        document.body.classList.remove('ov-lock');
        document.documentElement.classList.remove('ov-lock');
      }
    };

    const openZoom = () => {
      const media = frameSrc(frames[focus]);
      if (media.type === 'video' || !media.src) return;
      window.ovCart?.close?.();
      qs('[data-ov-checkout]')?.classList.remove('is-open');
      if (zoomEl.parentElement !== document.body) document.body.appendChild(zoomEl);
      zoomEl._ovZoomSession = {
        root,
        close: closeZoom,
        prev: () => { step(-1); setZoomImage(); },
        next: () => { step(1); setZoomImage(); }
      };
      clearZoomImage(zoomEl);
      zoomEl.classList.add('is-open');
      zoomEl.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ov-lock');
      document.documentElement.classList.add('ov-lock');
      setZoomImage();
    };

    zoomBtn?.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      openZoom();
    });
    mainStage.addEventListener('click', e => {
      if (e.target.closest('[data-pdp-main-prev], [data-pdp-main-next], [data-pdp-zoom]')) return;
      if (e.target.closest('video')) return;
      openZoom();
    });

    const selectFromPointer = e => {
      if (!downFrame) return;
      const dx = Math.abs(e.clientX - downX);
      const dy = Math.abs(e.clientY - downY);
      const scrolled = Math.abs(viewport.scrollLeft - downScroll) > 8;
      const frame = downFrame;
      downFrame = null;
      if (scrolled || dx > 16 || dy > 16) return;
      const i = frames.indexOf(frame);
      if (i < 0) return;
      setFocus(i, { scroll: true, smooth: true, apply: true });
    };

    viewport.style.touchAction = 'pan-x pan-y pinch-zoom';
    viewport.style.overscrollBehaviorX = 'contain';

    viewport.addEventListener('pointerdown', e => {
      const frame = e.target.closest('[data-ov-gfilm-frame]');
      if (!frame || !viewport.contains(frame)) {
        downFrame = null;
        return;
      }
      downFrame = frame;
      downX = e.clientX;
      downY = e.clientY;
      downScroll = viewport.scrollLeft;
    }, { passive: true });

    viewport.addEventListener('pointerup', selectFromPointer);
    viewport.addEventListener('pointercancel', () => { downFrame = null; });

    // Capture-phase click — most reliable on iOS inside scroll areas
    gfilm.addEventListener('click', e => {
      const frame = e.target.closest('[data-ov-gfilm-frame]');
      if (!frame || !gfilm.contains(frame)) return;
      e.preventDefault();
      e.stopPropagation();
      const i = frames.indexOf(frame);
      if (i < 0) return;
      setFocus(i, { scroll: true, smooth: true, apply: true });
    }, true);

    const syncFromScroll = () => {
      if (lockScrollSync) {
        requestAnimationFrame(moveLaser);
        return;
      }
      const best = nearestIndex();
      if (best !== focus) setFocus(best, { scroll: false, apply: true });
      else requestAnimationFrame(moveLaser);
    };

    viewport.addEventListener('scroll', syncFromScroll, { passive: true });
    viewport.addEventListener('scrollend', syncFromScroll);

    let touchStartX = 0;
    let touchStartY = 0;
    mainWrap.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    mainWrap.addEventListener('touchend', e => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) < 44 || Math.abs(dx) < Math.abs(dy)) return;
      setFocus(focus + (dx < 0 ? 1 : -1), { scroll: true, smooth: true, apply: true });
    }, { passive: true });

    window.addEventListener('resize', () => {
      if (focus < 0) return;
      setFocus(focus, { scroll: true, smooth: false, apply: false });
    });

    const srcKey = url => {
      if (!url) return '';
      const clean = String(url).split('?')[0];
      const file = clean.split('/').pop() || clean;
      return file.replace(/_\d+x\d*/g, '').replace(/\.(jpg|jpeg|png|webp|gif)$/i, '');
    };

    const frameColorHay = (frame) => {
      const thumb = (frame.getAttribute('data-pdp-thumb') || '').toLowerCase();
      const color = (frame.getAttribute('data-color') || '').toLowerCase();
      const alt = (qs('img', frame)?.getAttribute('alt') || '').toLowerCase();
      return `${color} ${alt} ${thumb} ${srcKey(thumb)}`;
    };

    const colorTokens = (val) => {
      const v = String(val || '').toLowerCase();
      if (!v) return [];
      if (v.includes('silver') || v.includes('white')) return ['silver', 'white'];
      if (v.includes('rose')) return ['rose', 'rosegold', 'rose-gold'];
      if (v.includes('gold') || v.includes('golden') || v.includes('yellow')) {
        return ['gold', 'golden', 'yellowgold', 'yellow-gold', 'yellow'];
      }
      return [v];
    };

    // Variant picker uses this to jump / filter gallery for selected color
    root._ovGallery = {
      focusMediaId(id) {
        if (id == null || id === '') return false;
        const sid = String(id).replace(/\D/g, '') || String(id);
        const i = frames.findIndex(f => {
          const fid = String(f.getAttribute('data-media-id') || '');
          return fid === String(id) || fid.replace(/\D/g, '') === sid;
        });
        if (i < 0) return false;
        frames.forEach(f => { f.hidden = false; f.classList.remove('is-color-hidden'); });
        setFocus(i, { scroll: true, smooth: true, apply: true });
        return true;
      },
      focusSrc(src) {
        if (!src) return false;
        const key = srcKey(src);
        const i = frames.findIndex(f => {
          const t = f.getAttribute('data-pdp-thumb') || '';
          if (!t) return false;
          if (t === src) return true;
          const tk = srcKey(t);
          return !!(key && tk && (key === tk || t.includes(key) || src.includes(tk)));
        });
        if (i < 0) return false;
        frames.forEach(f => { f.hidden = false; f.classList.remove('is-color-hidden'); });
        setFocus(i, { scroll: true, smooth: true, apply: true });
        return true;
      },
      filterByColor(colorValue) {
        const tokens = colorTokens(colorValue);
        if (!tokens.length) return false;
        const matches = [];
        frames.forEach((f, i) => {
          const hay = frameColorHay(f);
          const ok = tokens.some(t => hay.includes(t));
          // Avoid marking generic "gold" hits when selecting silver, etc.
          if (ok) matches.push(i);
        });
        if (!matches.length) {
          frames.forEach(f => { f.hidden = false; f.classList.remove('is-color-hidden'); });
          return false;
        }
        frames.forEach((f, i) => {
          const show = matches.includes(i);
          f.hidden = !show;
          f.classList.toggle('is-color-hidden', !show);
        });
        setFocus(matches[0], { scroll: true, smooth: true, apply: true });
        gfilm.classList.toggle('is-compact', !needsScroll());
        requestAnimationFrame(moveLaser);
        return true;
      }
    };

    let booted = false;
    const bootGallery = () => {
      gfilm.classList.add('is-ready');
      gfilm.classList.toggle('is-compact', !needsScroll());
      if (!booted) {
        // First paint only — later boots must not wipe variant-selected image
        setFocus(0, { scroll: needsScroll(), smooth: false, apply: true });
        booted = true;
      } else {
        const i = focus < 0 ? 0 : focus;
        setFocus(i, { scroll: true, smooth: false, apply: false });
      }
      requestAnimationFrame(moveLaser);
    };

    // Remeasure after overlay/layout settles so filmstrip + laser land correctly
    bootGallery();
    requestAnimationFrame(() => bootGallery());
    setTimeout(bootGallery, 100);
    setTimeout(bootGallery, 280);

    frames.forEach(frame => {
      const img = qs('img', frame);
      if (!img || img.complete) return;
      img.addEventListener('load', () => {
        if (focus === 0) setFocus(0, { scroll: true, smooth: false, apply: false });
        else moveLaser();
      }, { once: true });
    });
  }

  function initRelatedRail(root) {
    const rail = qs('[data-ov-rail]', root);
    if (!rail || rail.dataset.ovRailReady) return;
    rail.dataset.ovRailReady = '1';

    const viewport = qs('[data-ov-rail-viewport]', rail);
    const cells = qsa('[data-ov-rail-cell]', rail);
    const prev = qs('[data-ov-rail-prev]', rail);
    const next = qs('[data-ov-rail-next]', rail);
    const indexEl = qs('[data-ov-rail-index]', rail);
    const beam = qs('[data-ov-rail-beam]', rail);
    if (!viewport || !cells.length) return;

    let focus = 0;
    let dragX = 0;
    let startX = 0;
    let dragging = false;
    let scrollLeft = 0;
    let moved = false;

    const pad = n => String(n).padStart(2, '0');

    const updateFocus = () => {
      const vpRect = viewport.getBoundingClientRect();
      const center = vpRect.left + vpRect.width / 2;
      let best = 0;
      let bestDist = Infinity;
      cells.forEach((cell, i) => {
        const r = cell.getBoundingClientRect();
        const dist = Math.abs(r.left + r.width / 2 - center);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      focus = best;
      cells.forEach((cell, i) => {
        cell.classList.toggle('is-focus', i === best);
        cell.classList.toggle('is-left', i < best);
        cell.classList.toggle('is-right', i > best);
      });
      if (indexEl) indexEl.textContent = pad(best + 1);
      if (beam) beam.style.width = `${((best + 1) / cells.length) * 100}%`;
      if (prev) prev.disabled = best === 0;
      if (next) next.disabled = best === cells.length - 1;
    };

    const goTo = (i, smooth = true) => {
      const cell = cells[Math.max(0, Math.min(cells.length - 1, i))];
      if (!cell) return;
      scrollToCenter(viewport, cell, smooth && !dragging);
      requestAnimationFrame(updateFocus);
    };

    prev?.addEventListener('click', () => goTo(focus - 1));
    next?.addEventListener('click', () => goTo(focus + 1));

    viewport.style.touchAction = 'pan-x pan-y pinch-zoom';
    viewport.style.overscrollBehaviorX = 'contain';

    viewport.addEventListener('scroll', () => requestAnimationFrame(updateFocus), { passive: true });
    window.addEventListener('resize', updateFocus);

    viewport.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(focus - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(focus + 1); }
    });

    if (!isMobileView()) {
      viewport.addEventListener('pointerdown', e => {
        if (e.target.closest('[data-ov-pdp-link], a, button')) return;
        dragging = true;
        moved = false;
        dragX = 0;
        startX = e.clientX;
        scrollLeft = viewport.scrollLeft;
        viewport.classList.add('is-dragging');
        viewport.setPointerCapture?.(e.pointerId);
      });

      viewport.addEventListener('pointermove', e => {
        if (!dragging) return;
        dragX = e.clientX - startX;
        if (Math.abs(dragX) > 8) moved = true;
        viewport.scrollLeft = scrollLeft - dragX;
        requestAnimationFrame(updateFocus);
      });

      const endDrag = e => {
        if (!dragging) return;
        dragging = false;
        viewport.classList.remove('is-dragging');
        try { viewport.releasePointerCapture?.(e.pointerId); } catch (_) {}
        if (moved && Math.abs(dragX) > 40) goTo(focus + (dragX < 0 ? 1 : -1));
        else if (!moved) goTo(focus);
        else goTo(focus);
        setTimeout(() => { moved = false; }, 0);
        dragX = 0;
      };
      viewport.addEventListener('pointerup', endDrag);
      viewport.addEventListener('pointercancel', endDrag);
    }

    // Complete the look — always open sheet overlay
    rail.addEventListener('click', e => {
      const link = e.target.closest('[data-ov-pdp-link]');
      if (!link || !rail.contains(link)) return;
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const slug = link.getAttribute('data-ov-pdp-link');
      if (!slug) return;
      if (typeof window.ovOpenPDP === 'function') {
        history.pushState({ ovPdp: slug }, '', '#' + slug);
        window.ovOpenPDP(slug);
        scrollPdpToTop(true);
      } else {
        location.hash = slug;
        scrollPdpToTop(true);
      }
    });

    updateFocus();
    requestAnimationFrame(() => goTo(0, false));
  }
  window.ovBindPDP = bindPDP;

  function initPDPPage() {
    initGlobalPDP();
    initProductPageRoute();
    initShopifyProductPage();
  }

  function initProductPageRoute() {
    const wrap = qs('[data-ov-pdp-page-wrap]');
    if (!wrap) return;

    const show = slug => {
      const s = slug || location.hash.replace('#', '') || 'aurelia-diamond-ring';
      qsa('[data-ov-pdp-page-item]', wrap).forEach(el => {
        const on = el.dataset.ovPdpPageItem === s;
        el.hidden = !on;
        el.classList.toggle('is-active', on);
      });
      const active = qs(`[data-ov-pdp-page-item="${s}"]`, wrap);
      if (active) bindPDP(active);
      setPageTitle(s);
      scrollPdpToTop(true);
    };

    window.ovShowProductPage = show;
    show();
    window.addEventListener('hashchange', () => show());
  }

  function initGlobalPDP() {
    const overlay = qs('[data-ov-pdp-global]');
    const globalMount = qs('[data-ov-pdp-global-mount]');
    const closeBtn = qs('[data-ov-pdp-global-close]');
    const store = qs('#ov-pdp-store');
    const baseTitle = document.title;

    /* Section anchors (#type-rings) must never open the product sheet */
    const isProductSlug = slug => {
      if (!slug || typeof slug !== 'string') return false;
      const s = slug.trim();
      if (!s || s.startsWith('type-') || s === 'all' || s === 'top') return false;
      if (document.getElementById(s)) return false;
      return /^[a-z0-9][a-z0-9\-]*$/i.test(s);
    };

    const stripReadyFlags = html => (html || '')
      .replace(/\sdata-ov-gfilm-ready="[^"]*"/gi, '')
      .replace(/\sdata-ov-rail-ready="[^"]*"/gi, '')
      .replace(/\sdata-ov-ft-crown-ready="[^"]*"/gi, '');

    const mountSheet = (html, slug, wasOpen) => {
      if (!html || !globalMount) return false;
      forceCloseZoom();
      globalMount.innerHTML = stripReadyFlags(html);
      qsa('[data-ov-gfilm-ready], [data-ov-rail-ready], [data-ov-ft-crest]', globalMount).forEach(el => {
        delete el.dataset.ovGfilmReady;
        delete el.dataset.ovRailReady;
        delete el.dataset.ovFtCrownReady;
      });
      bindPDP(globalMount);
      setPageTitle(slug);
      if (overlay) {
        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
      }
      document.body.classList.add('ov-lock');
      document.documentElement.classList.add('ov-lock');
      if (isMobileView()) {
        document.body.classList.add('ov-pdp-mobile-open');
        updateMobileAppBar(globalMount, slug);
      } else {
        hideMobileAppBar();
      }
      if (wasOpen) {
        requestAnimationFrame(() => scrollPdpToTop(true));
      } else if (overlay) {
        overlay.scrollTop = 0;
        window.scrollTo(0, 0);
      }
      setTimeout(() => window.dispatchEvent(new Event('resize')), 80);
      setTimeout(() => window.dispatchEvent(new Event('resize')), 280);
      return true;
    };

    const resolveSheetHtml = async slug => {
      const item = store ? qs(`[data-ov-pdp-store-item="${slug}"]`, store) : null;
      const fromStore = item?.innerHTML?.trim();
      if (fromStore && fromStore.length > 300) return fromStore;

      const p = getCatalog().find(x => x.slug === slug);
      if (p) return buildPDP(p);

      try {
        const res = await fetch(`/products/${encodeURIComponent(slug)}`);
        if (!res.ok) return '';
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const section = doc.querySelector('[data-ov-pdp-page]');
        if (!section) return '';
        let html = section.outerHTML;
        const bar = doc.querySelector('[data-pdp-bar]');
        if (bar) html += bar.outerHTML;
        return html;
      } catch (_) {
        return '';
      }
    };

    const open = async (slug, opts = {}) => {
      if (!slug || !globalMount || !isProductSlug(slug)) return false;
      window.ovCloseSearch?.();
      const wasOpen = !!(overlay && overlay.classList.contains('is-open'));
      const html = await resolveSheetHtml(slug);
      if (!html) {
        const fallback = opts.fallbackUrl || `/products/${encodeURIComponent(slug)}`;
        if (opts.allowNavigate !== false) window.location.assign(fallback);
        return false;
      }
      return mountSheet(html, slug, wasOpen);
    };

    const close = () => {
      forceCloseZoom();
      if (overlay) {
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
      }
      hideMobileAppBar();
      document.body.classList.remove('ov-pdp-mobile-open');
      if (globalMount) globalMount.innerHTML = '';
      const cartOpen = qs('[data-ov-cart].is-open');
      const searchOpen = qs('[data-ov-search].is-open');
      if (!cartOpen && !searchOpen) {
        document.body.classList.remove('ov-lock');
        document.documentElement.classList.remove('ov-lock');
      }
      document.title = baseTitle;
      if (qs('[data-ov-pdp-page-wrap]')) return;
      if (location.hash && isProductSlug(location.hash.replace('#', ''))) {
        history.pushState(null, '', location.pathname + location.search);
      }
    };

    initMobileAppBar(close);

    window.ovOpenPDP = open;
    window.ovClosePDP = close;

    const onProductClick = e => {
      const link = e.target.closest('[data-ov-pdp-link]');
      if (!link) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const slug = link.getAttribute('data-ov-pdp-link');
      if (!slug || !isProductSlug(slug)) return;

      // Already on a full product page — let normal links work
      if (document.body.classList.contains('template-product') && !qs('[data-ov-pdp-global].is-open')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      window.ovCart?.close();
      window.ovCart?.closeCheckout();
      window.ovCloseSearch?.();

      if (qs('[data-ov-pdp-page-wrap]')) {
        location.hash = slug;
        return;
      }

      const fallbackUrl = link.getAttribute('href') || `/products/${encodeURIComponent(slug)}`;
      history.pushState({ ovPdp: slug }, '', '#' + slug);
      open(slug, { fallbackUrl });
    };

    document.addEventListener('click', onProductClick, true);

    closeBtn?.addEventListener('click', close);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });

    window.addEventListener('popstate', () => {
      if (qs('[data-ov-pdp-page-wrap]')) return;
      const slug = location.hash.replace('#', '');
      if (isProductSlug(slug)) open(slug, { allowNavigate: false });
      else close();
    });

    window.addEventListener('hashchange', () => {
      if (qs('[data-ov-pdp-page-wrap]')) return;
      const slug = location.hash.replace('#', '');
      if (isProductSlug(slug)) open(slug, { allowNavigate: false });
      else if (!slug) close();
    });

    const hash = location.hash.replace('#', '');
    if (isProductSlug(hash) && !qs('[data-ov-pdp-page-wrap]')) {
      open(hash, { allowNavigate: false });
    }
  }

  /* ---- Premium carousel ---- */
  function initNativeScrollCarousel(viewport, prev, next, progress) {
    if (!viewport || viewport.dataset.ovNativeScroll) return;
    viewport.dataset.ovNativeScroll = '1';

    const track = viewport.querySelector('[data-ov-carousel-track], [data-ov-gfilm-track], .ov-gfilm__track');
    const slides = track ? [...track.children].filter(el => el.matches('[data-ov-carousel-slide], [data-ov-gfilm-frame]') || el.classList.contains('ov-gfilm__frame')) : [];

    const scrollStep = () => {
      const slide = slides[0];
      if (!slide) return viewport.clientWidth * 0.5;
      const gap = parseFloat(getComputedStyle(track).gap) || 8;
      return slide.offsetWidth + gap;
    };

    const updateUI = () => {
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      if (progress) {
        progress.style.width = max <= 0 ? '100%' : `${(viewport.scrollLeft / max) * 100}%`;
      }
      if (prev) prev.disabled = viewport.scrollLeft <= 2;
      if (next) next.disabled = viewport.scrollLeft >= max - 2;
    };

    let touchX = 0;
    let touchY = 0;
    let swiped = false;

    viewport.addEventListener('touchstart', e => {
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
      swiped = false;
    }, { passive: true });

    viewport.addEventListener('touchmove', e => {
      const dx = Math.abs(e.touches[0].clientX - touchX);
      const dy = Math.abs(e.touches[0].clientY - touchY);
      if (dx > 12 && dx > dy) swiped = true;
    }, { passive: true });

    viewport.addEventListener('click', e => {
      /* Only block link navigation after a real swipe — never stopPropagation,
         so product PDP clicks still reach the document handler. */
      if (swiped && e.target.closest('a')) {
        e.preventDefault();
      }
      swiped = false;
    }, true);

    viewport.addEventListener('scroll', updateUI, { passive: true });
    window.addEventListener('resize', updateUI);

    prev?.addEventListener('click', e => {
      e.preventDefault();
      viewport.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
    });
    next?.addEventListener('click', e => {
      e.preventDefault();
      viewport.scrollBy({ left: scrollStep(), behavior: 'smooth' });
    });

    requestAnimationFrame(updateUI);
  }

  function initCarousels() {
    /* Premium + collection sliders: native scroll on all viewports
       so vertical page scroll is never captured by drag handlers. */
    const useNativeScroll = root =>
      root.classList.contains('ov-cslider__carousel') ||
      root.classList.contains('ov-pslider__carousel') ||
      root.closest('.ov-pslider') != null ||
      window.matchMedia('(max-width: 749px)').matches;

    qsa('[data-ov-carousel]').forEach(root => {
      if (root.dataset.ovCarouselReady) return;

      const track = qs('[data-ov-carousel-track]', root);
      const slides = qsa('[data-ov-carousel-slide]', root);
      const prev = qs('[data-ov-carousel-prev]', root);
      const next = qs('[data-ov-carousel-next]', root);
      const progress = qs('[data-ov-carousel-progress]', root);
      const viewport = qs('[data-ov-carousel-viewport]', root) || qs('.ov-carousel__viewport', root);
      if (!track || !slides.length) return;

      if (track.classList.contains('ov-showcase__scroll')) return;

      root.dataset.ovCarouselReady = '1';

      if (useNativeScroll(root) && viewport) {
        track.style.transform = 'none';
        track.style.transition = 'none';
        initNativeScrollCarousel(viewport, prev, next, progress);
        return;
      }

      let index = 0;
      let dragX = 0;
      let startX = 0;
      let dragging = false;
      const autoplayMs = parseInt(root.dataset.ovCarouselAutoplay || '0', 10);
      let timer;

      const gap = () => parseFloat(getComputedStyle(track).gap) || 14;
      const slideW = () => slides[0].offsetWidth;
      const maxIndex = () => Math.max(0, slides.length - (window.innerWidth < 750 ? 1 : window.innerWidth < 1024 ? 2 : 3));

      const setX = (dragOffset = 0) => {
        track.style.transform = `translate3d(${-(index * (slideW() + gap())) + dragOffset}px, 0, 0)`;
        if (progress) progress.style.width = `${((index + 1) / (maxIndex() + 1)) * 100}%`;
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index >= maxIndex();
      };

      const go = dir => {
        index = Math.max(0, Math.min(maxIndex(), index + dir));
        track.style.transition = 'transform 0.65s cubic-bezier(0.16,1,0.3,1)';
        setX(0);
        resetAutoplay();
      };

      const resetAutoplay = () => {
        if (!autoplayMs) return;
        clearInterval(timer);
        timer = setInterval(() => go(index >= maxIndex() ? -maxIndex() : 1), autoplayMs);
      };

      prev?.addEventListener('click', () => go(-1));
      next?.addEventListener('click', () => go(1));
      window.addEventListener('resize', () => { index = Math.min(index, maxIndex()); setX(0); });

      const dragTarget = viewport || track;
      dragTarget.style.touchAction = 'pan-x pan-y pinch-zoom';

      const isInteractive = e => e.target.closest('[data-ov-pdp-link], a, button');

      const startDrag = clientX => {
        dragging = true;
        startX = clientX;
        dragX = 0;
        track.style.transition = 'none';
      };

      const moveDrag = clientX => {
        if (!dragging) return;
        dragX = clientX - startX;
        setX(-dragX);
      };

      const endDrag = () => {
        if (!dragging) return;
        dragging = false;
        track.style.transition = 'transform 0.65s cubic-bezier(0.16,1,0.3,1)';
        if (dragX < -44) go(1);
        else if (dragX > 44) go(-1);
        else setX(0);
        dragX = 0;
      };

      dragTarget.addEventListener('pointerdown', e => {
        if (isInteractive(e)) return;
        startDrag(e.clientX);
        dragTarget.setPointerCapture?.(e.pointerId);
      });
      dragTarget.addEventListener('pointermove', e => {
        if (!dragging) return;
        moveDrag(e.clientX);
      });
      dragTarget.addEventListener('pointerup', e => {
        dragTarget.releasePointerCapture?.(e.pointerId);
        endDrag();
      });
      dragTarget.addEventListener('pointercancel', endDrag);

      dragTarget.addEventListener('touchstart', e => {
        if (isInteractive(e)) return;
        startDrag(e.touches[0].clientX);
      }, { passive: true });
      dragTarget.addEventListener('touchmove', e => {
        if (!dragging) return;
        moveDrag(e.touches[0].clientX);
      }, { passive: true });
      dragTarget.addEventListener('touchend', endDrag, { passive: true });
      dragTarget.addEventListener('touchcancel', endDrag, { passive: true });

      setX(0);
      resetAutoplay();
    });
  }

  function initSearch() {
    const el = qs('[data-ov-search]');
    if (!el) return;
    const input = qs('[data-ov-search-input]', el);
    const results = qs('[data-ov-search-results]', el);
    const open = () => {
      el.classList.add('is-open');
      document.body.classList.add('ov-lock', 'ov-overlay-open');
      document.documentElement.classList.add('ov-lock');
      setTimeout(() => input?.focus(), 100);
    };
    const close = () => {
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      if (!qs('[data-ov-cart].is-open') && !qs('[data-ov-pdp-global].is-open')) {
        document.body.classList.remove('ov-lock', 'ov-overlay-open');
        document.documentElement.classList.remove('ov-lock');
      } else {
        document.body.classList.remove('ov-overlay-open');
      }
    };
    window.ovCloseSearch = close;
    const renderResults = q => {
      if (!results) return;
      const query = (q || '').trim().toLowerCase();
      if (query.length < 1) {
        results.innerHTML = '<p class="ov-search__hint">Try “ring”, “gold”, or a piece name.</p>';
        return;
      }
      const hits = getCatalog().filter(p =>
        [p.title, p.subtitle, p.material, p.category].join(' ').toLowerCase().includes(query)
      ).slice(0, 8);
      if (!hits.length) {
        results.innerHTML = '<p class="ov-search__hint">No pieces found.</p>';
        return;
      }
      results.innerHTML = hits.map(p =>
        `<a href="${p.url || ('/products/' + encodeURIComponent(p.slug))}" class="ov-search__hit" data-ov-pdp-link="${p.slug}">
          <img src="${p.img}" alt="" width="56" height="70" loading="lazy">
          <span><strong>${escapeHtml(displayTitle(p.title))}</strong><em>${escapeHtml(p.subtitle || '')}</em></span>
          <span class="ov-search__hit-price">${p.price}</span>
        </a>`
      ).join('');
    };
    input?.addEventListener('input', () => renderResults(input.value));
    qsa('[data-ov-search-open]').forEach(b => b.addEventListener('click', e => { e.preventDefault(); open(); renderResults(''); }));
    qsa('[data-ov-search-close], [data-ov-search-backdrop]', el).forEach(b => b.addEventListener('click', close));
    // Capture: product click handler stops propagation, so close search here first
    results?.addEventListener('click', e => {
      if (e.target.closest('[data-ov-pdp-link]')) close();
    }, true);
  }

  function initSizeGuideLinks() {
    document.addEventListener('click', e => {
      const a = e.target.closest('[data-ov-size-guide-link], a[href="/pages/size-guide"], a[href*="/pages/size-guide"]');
      if (!a) return;
      e.preventDefault();
      e.stopPropagation();
      try { window.ovClosePDP?.(); } catch (_) {}
      window.location.assign('/pages/size-guide');
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('is-loaded');
    scrubDocumentTitles();
    initReveal();
    initHeroSlider();
    initNav();
    initSearch();
    initPDPPage();
    initCarousels();
    initFooterCrown();
    initCategoryGrids();
    initTypeNav();
    initSizeGuideLinks();
    migrateWishList();
    initWishlistPage();
    syncWishlistUI();
    window.ovCart = new Cart();
    window.ovCart.count();
    window.ovCart.renderCartPage();
  });
  scrubDocumentTitles();

  function initTypeNav() {
    const nav = qs('[data-ov-type-nav]');
    if (!nav) return;
    const links = qsa('[data-ov-type-link]', nav);
    const sections = qsa('[data-ov-type-section]');
    if (!links.length || !sections.length) return;

    const setActive = id => {
      links.forEach(link => {
        const on = link.getAttribute('data-ov-type-link') === id;
        link.classList.toggle('is-active', on);
      });
      sections.forEach(sec => {
        sec.classList.toggle('is-active', sec.id === id);
      });
    };

    const scrollToId = id => {
      const target = document.getElementById(id);
      if (!target) return;
      setActive(id);
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      try {
        history.replaceState(null, '', `#${id}`);
      } catch (_) { /* ignore */ }
    };

    links.forEach(link => {
      link.addEventListener('click', e => {
        const id = link.getAttribute('data-ov-type-link');
        if (!id) return;
        e.preventDefault();
        scrollToId(id);
      });
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        entries => {
          const visible = entries
            .filter(e => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (visible[0]?.target?.id) setActive(visible[0].target.id);
        },
        { rootMargin: '-20% 0px -55% 0px', threshold: [0.1, 0.25, 0.5] }
      );
      sections.forEach(sec => io.observe(sec));
    }

    const hash = (location.hash || '').replace(/^#/, '');
    if (hash && document.getElementById(hash)) {
      requestAnimationFrame(() => scrollToId(hash));
    } else {
      setActive(sections[0].id);
    }
  }

  function initCategoryGrids() {
    /* Liquid already filters by product.type — do not remove cards via JS catalog.
       That was emptying View-all pages when catalog category/slug mismatched. */
  }

  function initWishlistPage() {
    const mount = qs('[data-ov-wishlist-mount]');
    syncWishlistUI();
    if (!mount) return;

    const render = () => {
      const list = wishList();
      const items = list.map(slug => getCatalog().find(p => p.slug === slug)).filter(Boolean);
      if (!items.length) {
        mount.innerHTML = `<div class="ov-cart__empty--page"><h3>No saved pieces yet</h3><p>Tap “Add to wishlist” on any product to save it here.</p><a href="/collections/all" class="ov-btn ov-btn--fill ov-btn--full">Browse collection</a></div>`;
        return;
      }
      mount.innerHTML = `<div class="ov-wish-grid">${items.map(p =>
        `<article class="ov-wish-card" data-wish-slug="${escapeHtml(p.slug)}">
          <a href="#${escapeHtml(p.slug)}" class="ov-wish-card__media" data-ov-pdp-link="${escapeHtml(p.slug)}">
            <img src="${escapeHtml(p.img)}" alt="${escapeHtml(displayTitle(p.title))}" loading="lazy" width="600" height="750">
          </a>
          <div class="ov-wish-card__info">
            <a href="#${escapeHtml(p.slug)}" class="ov-wish-card__title" data-ov-pdp-link="${escapeHtml(p.slug)}">${escapeHtml(displayTitle(p.title))}</a>
            <p class="ov-wish-card__sub">${escapeHtml(p.subtitle || '')}</p>
            <p class="ov-wish-card__price">${escapeHtml(p.price)}</p>
            <div class="ov-wish-card__actions">
              <button type="button" class="ov-btn ov-btn--fill" data-wish-add="${escapeHtml(p.slug)}">Add to bag</button>
              <button type="button" class="ov-btn ov-btn--outline" data-wish-remove="${escapeHtml(p.slug)}">Remove</button>
            </div>
          </div>
        </article>`
      ).join('')}</div>`;
    };

    mount.addEventListener('click', e => {
      const add = e.target.closest('[data-wish-add]');
      const remove = e.target.closest('[data-wish-remove]');
      if (add) {
        e.preventDefault();
        window.ovCart?.addItem(add.dataset.wishAdd, 1);
      } else if (remove) {
        e.preventDefault();
        toggleWishlist(remove.dataset.wishRemove);
        render();
        window.ovCart?.toast('Removed from wishlist');
      }
    });

    render();
  }

  function initMegaMenu() {
    const fly = qs('[data-ov-fly]');
    if (!fly) return;
    const items = qsa('[data-ov-fly-item]', fly);
    let closeTimer = null;

    const headerTop = () => {
      /* Use bar bottom — full header box is taller and creates a dead hover gap */
      const bar = qs('.ov-hd__bar') || qs('[data-ov-header]');
      const bottom = bar ? bar.getBoundingClientRect().bottom : 72;
      document.documentElement.style.setProperty('--ov-fly-top', `${Math.max(48, Math.round(bottom))}px`);
    };

    const updateFlyCarousel = item => {
      const rail = qs('[data-ov-fly-rail]', item);
      const prev = qs('[data-ov-fly-prev]', item);
      const next = qs('[data-ov-fly-next]', item);
      const progress = qs('[data-ov-fly-progress]', item);
      const controls = qs('.ov-fly__controls', item);
      if (!rail) return;
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const needs = max > 4;
      if (controls) controls.hidden = !needs;
      if (progress) progress.style.width = max <= 0 ? '100%' : `${(rail.scrollLeft / max) * 100}%`;
      if (prev) prev.disabled = !needs || rail.scrollLeft <= 2;
      if (next) next.disabled = !needs || rail.scrollLeft >= max - 2;
    };

    const resetRailScroll = item => {
      const rail = qs('[data-ov-fly-rail]', item);
      if (rail) rail.scrollLeft = 0;
      requestAnimationFrame(() => updateFlyCarousel(item));
    };

    const bindFlyCarousel = item => {
      const rail = qs('[data-ov-fly-rail]', item);
      if (!rail || rail.dataset.ovFlyScroll) return;
      rail.dataset.ovFlyScroll = '1';
      const track = qs('[data-ov-fly-track]', item);
      const prev = qs('[data-ov-fly-prev]', item);
      const next = qs('[data-ov-fly-next]', item);

      const step = () => {
        const card = qsa('.ov-fly__card', track).find(c => !c.hidden);
        if (!card) return rail.clientWidth * 0.55;
        const gap = parseFloat(getComputedStyle(track).gap) || 14;
        return card.offsetWidth + gap;
      };

      rail.addEventListener('wheel', e => {
        if (rail.scrollWidth <= rail.clientWidth + 2) return;
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
          rail.scrollLeft += e.deltaY;
        }
      }, { passive: false });

      let dragging = false;
      let moved = false;
      let startX = 0;
      let startLeft = 0;
      rail.addEventListener('pointerdown', e => {
        if (e.button != null && e.button !== 0) return;
        if (rail.scrollWidth <= rail.clientWidth + 2) return;
        if (e.target.closest('a, button')) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startLeft = rail.scrollLeft;
        rail.classList.add('is-dragging');
        try { rail.setPointerCapture(e.pointerId); } catch (_) {}
      });
      rail.addEventListener('pointermove', e => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        rail.scrollLeft = startLeft - dx;
      });
      const endDrag = e => {
        if (!dragging) return;
        dragging = false;
        rail.classList.remove('is-dragging');
        try { rail.releasePointerCapture?.(e.pointerId); } catch (_) {}
        if (moved) {
          const block = ev => {
            ev.preventDefault();
            ev.stopPropagation();
            rail.removeEventListener('click', block, true);
          };
          rail.addEventListener('click', block, true);
          setTimeout(() => rail.removeEventListener('click', block, true), 0);
        }
        moved = false;
      };
      rail.addEventListener('pointerup', endDrag);
      rail.addEventListener('pointercancel', endDrag);

      rail.addEventListener('scroll', () => updateFlyCarousel(item), { passive: true });
      prev?.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        rail.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      next?.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        rail.scrollBy({ left: step(), behavior: 'smooth' });
      });
      updateFlyCarousel(item);
    };

    const tabAnimFrames = new WeakMap();

    const tabLabel = (item, key) => {
      const tab = qs(`[data-fly-tab="${key}"]`, item);
      return (tab?.textContent || key || 'Category').trim();
    };

    const revealCards = item => {
      const cards = qsa('.ov-fly__card', item).filter(c => !c.hidden);
      const empty = qs('[data-ov-fly-empty]', item);
      const nodes = empty && !empty.hidden ? [empty, ...cards] : cards;
      nodes.forEach((el, i) => {
        el.classList.remove('is-in');
        el.style.setProperty('--ov-fly-i', String(Math.min(i, 12)));
      });
      const prev = tabAnimFrames.get(item);
      if (prev) {
        cancelAnimationFrame(prev.a);
        cancelAnimationFrame(prev.b);
      }
      const a = requestAnimationFrame(() => {
        void item.offsetWidth;
        const b = requestAnimationFrame(() => {
          nodes.forEach(el => el.classList.add('is-in'));
        });
        tabAnimFrames.set(item, { a, b });
      });
      tabAnimFrames.set(item, { a, b: 0 });
    };

    const applyFlyTab = (item, key) => {
      const handle = item.getAttribute('data-collection-handle') || '';
      const hub = item.getAttribute('data-ov-fly-hub') || '';
      const base = hub || (handle ? `/collections/${handle}` : '/collections/all');
      const viewAll = qs('[data-ov-fly-viewall]', item);
      const empty = qs('[data-ov-fly-empty]', item);
      const emptyTitle = qs('[data-ov-fly-empty-title]', item);
      const emptySub = qs('[data-ov-fly-empty-sub]', item);
      const want = (key || 'all').trim();

      qsa('.ov-fly__card', item).forEach(card => {
        card.classList.remove('is-in');
        const role = (card.getAttribute('data-fly-role') || '').trim();
        const pt = (card.getAttribute('data-product-type') || '').trim();
        let show = false;
        if (want === 'all') {
          /* All tab = collection/type cards */
          show = role === 'type';
        } else {
          /* Category tab = products for that type, or the type card as fallback */
          show = pt === want && (role === 'product' || role === 'type');
        }
        if (show) card.removeAttribute('hidden');
        else card.setAttribute('hidden', '');
      });

      const visible = qsa('.ov-fly__card', item).filter(c => !c.hasAttribute('hidden'));
      if (empty) {
        empty.classList.remove('is-in');
        const showEmpty = visible.length === 0;
        if (showEmpty) empty.removeAttribute('hidden');
        else empty.setAttribute('hidden', '');
        if (showEmpty) {
          const label = want === 'all' ? 'Collection' : tabLabel(item, want);
          if (emptyTitle) emptyTitle.textContent = `${label} — coming soon`;
          if (emptySub) {
            emptySub.textContent = want === 'all'
              ? 'Pieces for this maison are being prepared.'
              : `New ${label.toLowerCase()} are on the way.`;
          }
        }
      }

      if (viewAll) {
        const activeTab = qs(`[data-fly-tab="${want}"]`, item);
        const tabHref = activeTab?.getAttribute('href');
        const pageByType = {
          rings: '/pages/rings',
          bracelet: '/pages/bracelets',
          bangles: '/pages/bangles',
          'ear-rings': '/pages/earrings',
          'necklace-set': '/pages/necklaces',
          'locket-set': '/collections/locket-set',
          'bridel-set': '/collections/bridel-set',
          chains: '/collections/chains'
        };
        if (want === 'all') {
          viewAll.setAttribute('href', base || tabHref || '/collections/all');
          viewAll.textContent = 'View all';
        } else if (tabHref) {
          viewAll.setAttribute('href', tabHref);
          viewAll.textContent = 'View all in category';
        } else {
          viewAll.setAttribute('href', pageByType[want] || `/collections/${want}`);
          viewAll.textContent = 'View all in category';
        }
      }
      resetRailScroll(item);
      revealCards(item);
    };

    const setMegaHeader = on => {
      const hd = qs('[data-ov-header]');
      document.body.classList.toggle('ov-mega-open', on);
      document.documentElement.classList.toggle('ov-mega-open', on);
      hd?.classList.toggle('is-mega-open', on);
    };

    let activeItem = null;
    let lastPointer = { x: 0, y: 0 };

    const cancelClose = () => clearTimeout(closeTimer);
    const scheduleClose = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        if (isOverMega(lastPointer.x, lastPointer.y)) return;
        closeAll();
      }, 140);
    };

    /* Hit-test trigger + panel only (not the whole page width / upper viewport). */
    const pointIn = (el, x, y, pad = 0) => {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return x >= r.left - pad && x <= r.right + pad && y >= r.top - pad && y <= r.bottom + pad;
    };

    const isOverMega = (x, y) => {
      if (!activeItem || x == null || y == null) return false;
      const trigger = qs('[data-ov-fly-trigger]', activeItem);
      const panel = qs('[data-ov-fly-panel]', activeItem);
      if (pointIn(trigger, x, y, 4)) return true;
      if (panel && !panel.hasAttribute('hidden') && pointIn(panel, x, y, 6)) return true;
      /* Thin bridge between bar and panel so the gap doesn't flicker-close */
      if (trigger && panel && !panel.hasAttribute('hidden')) {
        const tr = trigger.getBoundingClientRect();
        const pr = panel.getBoundingClientRect();
        const left = Math.min(tr.left, pr.left);
        const right = Math.max(tr.right, pr.right);
        if (x >= left && x <= right && y >= tr.bottom && y <= pr.top + 2) return true;
      }
      return false;
    };

    const closeAll = () => {
      cancelClose();
      items.forEach(item => {
        item.classList.remove('is-open');
        qsa('.ov-fly__card, .ov-fly__empty', item).forEach(el => el.classList.remove('is-in'));
        const panel = qs('[data-ov-fly-panel]', item);
        const trigger = qs('[data-ov-fly-trigger]', item);
        if (panel) panel.setAttribute('hidden', '');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
      activeItem = null;
      setMegaHeader(false);
    };

    const openItem = item => {
      cancelClose();
      headerTop();

      if (activeItem === item && item.classList.contains('is-open')) {
        setMegaHeader(true);
        requestAnimationFrame(headerTop);
        return;
      }

      items.forEach(other => {
        if (other === item) return;
        other.classList.remove('is-open');
        qsa('.ov-fly__card, .ov-fly__empty', other).forEach(el => el.classList.remove('is-in'));
        const p = qs('[data-ov-fly-panel]', other);
        const t = qs('[data-ov-fly-trigger]', other);
        if (p) p.setAttribute('hidden', '');
        if (t) t.setAttribute('aria-expanded', 'false');
      });

      const panel = qs('[data-ov-fly-panel]', item);
      const trigger = qs('[data-ov-fly-trigger]', item);
      if (panel) panel.removeAttribute('hidden');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      if (!item.classList.contains('is-open')) {
        item.classList.add('is-open');
        const activeTab = qs('[data-fly-tab].is-active', item) || qs('[data-fly-tab="all"]', item);
        if (activeTab) {
          qsa('[data-fly-tab]', item).forEach(t => t.classList.toggle('is-active', t === activeTab));
        }
        applyFlyTab(item, activeTab?.getAttribute('data-fly-tab') || 'all');
      }
      activeItem = item;
      setMegaHeader(true);
      requestAnimationFrame(() => {
        headerTop();
        requestAnimationFrame(headerTop);
      });
    };

    items.forEach(item => {
      const panel = qs('[data-ov-fly-panel]', item);
      const trigger = qs('[data-ov-fly-trigger]', item);

      trigger?.addEventListener('mouseenter', () => openItem(item));
      panel?.addEventListener('mouseenter', () => {
        cancelClose();
        openItem(item);
      });
      trigger?.addEventListener('focus', () => openItem(item));

      qsa('[data-fly-tab]', item).forEach(tab => {
        const activate = () => {
          cancelClose();
          if (activeItem !== item) openItem(item);
          qsa('[data-fly-tab]', item).forEach(t => t.classList.toggle('is-active', t === tab));
          applyFlyTab(item, tab.getAttribute('data-fly-tab') || 'all');
        };
        tab.addEventListener('mouseenter', activate);
        tab.addEventListener('focus', activate);
        tab.addEventListener('click', e => {
          activate();
          const key = tab.getAttribute('data-fly-tab') || 'all';
          if (key !== 'all') e.preventDefault();
        });
      });

      applyFlyTab(item, 'all');
      bindFlyCarousel(item);
    });

    document.addEventListener('mousemove', e => {
      lastPointer = { x: e.clientX, y: e.clientY };
      if (!activeItem) return;
      if (isOverMega(e.clientX, e.clientY)) cancelClose();
      else scheduleClose();
    }, { passive: true });

    document.addEventListener('mouseleave', () => {
      if (activeItem) scheduleClose();
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeAll();
    });
    window.addEventListener('resize', headerTop, { passive: true });
    window.addEventListener('scroll', () => {
      if (activeItem) {
        headerTop();
        if (!isOverMega(lastPointer.x, lastPointer.y)) scheduleClose();
      }
    }, { passive: true });
    headerTop();
  }

  function initNav() {
    const menu = qs('[data-ov-nav]');
    const open = () => {
      if (!menu) return;
      menu.setAttribute('aria-hidden', 'false');
      menu.classList.add('is-open');
      document.body.classList.add('ov-lock');
      document.documentElement.classList.add('ov-lock');
    };
    const close = () => {
      if (!menu) return;
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      if (!qs('[data-ov-cart].is-open') && !qs('[data-ov-search].is-open') && !qs('[data-ov-pdp-global].is-open')) {
        document.body.classList.remove('ov-lock');
        document.documentElement.classList.remove('ov-lock');
      }
    };
    qsa('[data-ov-nav-open]').forEach(b => b.addEventListener('click', open));
    qsa('[data-ov-nav-close]').forEach(b => b.addEventListener('click', close));
    initMegaMenu();

    const openAccount = () => {
      close();
      const host = qs('shopify-account');
      const trigger =
        host?.shadowRoot?.querySelector('button, [role="button"], summary, .signed-out-avatar, [part="signed-out-avatar"]') ||
        host?.querySelector('[slot="signed-out-avatar"]') ||
        host;
      if (trigger?.click) trigger.click();
      else if (host?.click) host.click();
    };
    qsa('[data-ov-account-open]').forEach(b => b.addEventListener('click', e => {
      e.preventDefault();
      openAccount();
    }));
  }
})();







